import React, { useState, useEffect, useRef } from 'react';
import {
  Briefcase, FileText, Calendar, DollarSign, Users, Building2, ArrowLeft,
  Save, Send, Plus, Pencil, Trash2, Bell, Settings, LogOut, Moon, Sun,
  Image as ImageIcon, ChevronDown, ChevronUp, MoreVertical, Eye, X, Search,
  Filter, ArrowUpDown, Clock, Mail, Phone, Award, MessageSquare, CheckCircle,
  HelpCircle, Layers, AlertCircle, Lock, Star, Wrench, ExternalLink, Link2,
  Upload, Camera, Zap, GitMerge, RefreshCw
} from 'lucide-react';
import { useWorkdayClientTheme } from '../../contexts/WorkdayClientThemeContext';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import InterviewRequestModal from '../../components/workday-client/InterviewRequestModal';
import WorkdayClientHeader from '../../components/workday-client/WorkdayClientHeader';
import { transitionCandidateStage } from '../../lib/candidatePipeline';

// Returns null when the post is allowed, or an error message string when blocked.
async function checkPostQuotaServer(
  accessToken: string,
  supabaseUrl: string,
  anonKey: string,
  jobTitle: string,
  skillsQualifications: string
): Promise<string | null> {
  try {
    const res = await fetch(`${supabaseUrl}/functions/v1/check-post-quota`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        Apikey: anonKey,
      },
      body: JSON.stringify({ job_title: jobTitle, location: '', skills_qualifications: skillsQualifications }),
    });
    const json = await res.json();
    if (!res.ok) return json?.error || 'Quota check failed. Please try again.';
    if (json?.allowed === false) return json?.message || 'Post quota exceeded.';
    return null;
  } catch {
    return 'Unable to verify post quota. Please try again.';
  }
}

type PostingType = 'job' | 'service';
type ActiveView = 'list' | 'create-job' | 'create-service' | 'view-job' | 'view-service' | 'quick-post' | 'pipeline-post';

interface ApplicationQuestion {
  question: string;
}

interface JobPosting {
  id: string;
  requisition_number: string;
  job_title: string;
  hire_deadline_date: string;
  worker_type: string;
  job_description: string;
  supervisory_organization: string;
  job_family: string;
  skills_qualifications: string;
  compensation_amount: number;
  comp_grade: string;
  status: string;
  is_pro_posting?: boolean;
  application_questions?: ApplicationQuestion[];
  career_page_url?: string;
  created_at: string;
}

interface PositionRequirement {
  positions: string;
  type: string;
}

interface ServiceRequirement {
  id: string;
  project_title: string;
  position_requirements: PositionRequirement[];
  issues_and_modules: string;
  project_requirements: string;
  implementation_stages: string;
  implementation_partner: string;
  number_of_employees: string;
  go_live_date: string;
  hire_deadline_date: string;
  enhancements: string;
  created_at: string;
}

interface JobApplication {
  id: string;
  job_posting_id: string;
  consultant_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  title?: string;
  country_region: string;
  experience_years: string | number;
  workday_modules: string | string[];
  certifications?: string | string[];
  availability: string;
  hourly_rate: string | number;
  cover_letter?: string;
  message?: string;
  question_answers?: Array<{ question: string; answer: string }>;
  status: string;
  prescreen_status?: string | null;
  applied_at: string;
  reviewed_by?: string;
  reviewed_at?: string;
  admin_notes?: string;
  resume_url?: string;
}

const STATUS_COLORS: Record<string, string> = {
  open: 'bg-green-100 text-green-800',
  draft: 'bg-gray-100 text-gray-700',
  active: 'bg-blue-100 text-blue-800',
  filled: 'bg-teal-100 text-teal-800',
  closed: 'bg-red-100 text-red-700',
};

const APP_STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  reviewed: 'bg-blue-100 text-blue-800',
  shortlisted: 'bg-green-100 text-green-800',
  interview_scheduled: 'bg-teal-100 text-teal-800',
  rejected: 'bg-red-100 text-red-800',
  hired: 'bg-emerald-100 text-emerald-800',
};

export default function PostJobs() {
  const { theme, toggleTheme } = useWorkdayClientTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, signOut, userType } = useAuth();

  const isDark = theme === 'dark';

  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoLoading, setLogoLoading] = useState(true);
  const [notifications] = useState(3);
  const [postingType, setPostingType] = useState<PostingType>('job');
  const [activeView, setActiveView] = useState<ActiveView>('list');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [pipelineFormError, setPipelineFormError] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);

  const [jobPostings, setJobPostings] = useState<JobPosting[]>([]);
  const [serviceRequirements, setServiceRequirements] = useState<ServiceRequirement[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [viewingJob, setViewingJob] = useState<JobPosting | null>(null);
  const [viewingService, setViewingService] = useState<ServiceRequirement | null>(null);
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [applicationCounts, setApplicationCounts] = useState<{ [key: string]: number }>({});
  const [selectedTab, setSelectedTab] = useState<'details' | 'applications'>('details');
  const [loadingApplications, setLoadingApplications] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [requestingInterviewFor, setRequestingInterviewFor] = useState<JobApplication | null>(null);
  const [sendingPrescreen, setSendingPrescreen] = useState<string | null>(null);
  const [prescreenSent, setPrescreenSent] = useState<Set<string>>(new Set());
  const [clientCompanyName, setClientCompanyName] = useState<string>('Hiring Company');
  // Schedule Pre-Screen modal (initial request with time slots)
  const [scheduleFor, setScheduleFor] = useState<JobApplication | null>(null);
  const [scheduleSlots, setScheduleSlots] = useState([{ date: '', time: '' }, { date: '', time: '' }, { date: '', time: '' }]);
  const [scheduleNote, setScheduleNote] = useState('');
  // Confirm slot modal (client reviews consultant's submitted availability)
  const [confirmFor, setConfirmFor] = useState<{ app: JobApplication; slots: Array<{ date: string; time: string }>; msgId: string } | null>(null);
  const [confirmingSlot, setConfirmingSlot] = useState(false);
  // Client propose-new-times modal
  const [proposeTimesFor, setProposeTimesFor] = useState<JobApplication | null>(null);
  const [proposeSlots, setProposeSlots] = useState([{ date: '', time: '' }, { date: '', time: '' }, { date: '', time: '' }]);
  const [proposeNote, setProposeNote] = useState('');
  const [sendingPropose, setSendingPropose] = useState(false);
  const [showProUpgradePopup, setShowProUpgradePopup] = useState(false);
  const [showPostRequisitionPopup, setShowPostRequisitionPopup] = useState(false);
  // adminPlan param is only ever set by the ATS Testing page — safe to use directly
  const adminPlanOverride = searchParams.get('adminPlan');
  const [isPro, setIsPro] = useState(() => adminPlanOverride === 'pro');
  // true while the plan fetch is in-flight; prevents the locked card from flashing on Pro accounts
  const [planLoading, setPlanLoading] = useState(() => adminPlanOverride === null);

  const [jobSearchTerm, setJobSearchTerm] = useState('');
  const [jobSortBy, setJobSortBy] = useState<'date' | 'deadline' | 'status'>('date');
  const [jobFilterStatus, setJobFilterStatus] = useState<'all' | 'draft' | 'open' | 'active' | 'filled' | 'closed'>('all');
  const [serviceSearchTerm, setServiceSearchTerm] = useState('');
  const [serviceSortBy, setServiceSortBy] = useState<'date' | 'deadline'>('date');

  const [clientProjectRequirements, setClientProjectRequirements] = useState('');

  // Company brand images (LinkedIn-style)
  const [backgroundImageUrl, setBackgroundImageUrl] = useState<string | null>(null);
  const [companyProfileImageUrl, setCompanyProfileImageUrl] = useState<string | null>(null);
  const [uploadingBackground, setUploadingBackground] = useState(false);
  const [uploadingProfile, setUploadingProfile] = useState(false);
  const backgroundInputRef = useRef<HTMLInputElement>(null);
  const profileInputRef = useRef<HTMLInputElement>(null);

  // Quick post (Pro-only requisition link flow)
  const [quickPostForm, setQuickPostForm] = useState({
    requisition_number: '',
    job_title: '',
    hire_deadline_date: '',
    worker_type: 'hire',
    supervisory_organization: '',
    job_family: '',
    compensation_amount: '',
    comp_grade: '',
    career_page_url: '',
  });

  // Pipeline post (Pro-only full requisition via PerplexityATS Interview-to-Offer)
  const [pipelinePostForm, setPipelinePostForm] = useState({
    requisition_number: '',
    job_title: '',
    hire_deadline_date: '',
    worker_type: 'hire',
    job_description: '',
    supervisory_organization: '',
    job_family: '',
    skills_qualifications: '',
    compensation_amount: '',
    comp_grade: '',
    status: 'open',
    application_questions: [{ question: '' }] as ApplicationQuestion[],
  });

  const [jobForm, setJobForm] = useState({
    requisition_number: '',
    job_title: '',
    hire_deadline_date: '',
    worker_type: 'hire',
    job_description: '',
    supervisory_organization: '',
    job_family: '',
    skills_qualifications: '',
    compensation_amount: '',
    comp_grade: '',
    status: 'open',
    is_pro_posting: false,
    career_page_url: '',
    application_questions: [{ question: '' }] as ApplicationQuestion[]
  });

  const [serviceForm, setServiceForm] = useState({
    project_title: '',
    position_requirements: [{ positions: '', type: '' }] as PositionRequirement[],
    issues_and_modules: '',
    project_requirements: '',
    implementation_stages: '',
    implementation_partner: '',
    number_of_employees: '',
    go_live_date: '',
    hire_deadline_date: '',
    enhancements: ''
  });

  // Re-evaluate plan whenever adminPlan param changes (ATS Testing toggle) or user changes
  useEffect(() => {
    if (adminPlanOverride === 'pro') {
      setIsPro(true);
      setPlanLoading(false);
    } else if (adminPlanOverride === null) {
      fetchPlan();
    } else {
      setIsPro(false);
      setPlanLoading(false);
    }
  }, [adminPlanOverride, user?.id]);

  useEffect(() => {
    fetchLogo();
    fetchData();
    fetchClientProfile();

    if (!user) return;
    const channel = supabase
      .channel('post_jobs_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'workday_client_job_postings', filter: `customer_id=eq.${user.id}` }, fetchData)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'workday_client_service_requirements', filter: `customer_id=eq.${user.id}` }, fetchData)
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (openDropdown && !(e.target as Element).closest('.relative')) setOpenDropdown(null);
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [openDropdown]);

  useEffect(() => {
    if (activeView === 'list' && adminPlanOverride === null) {
      fetchPlan();
    }
  }, [activeView]);

  useEffect(() => {
    if (viewingJob) fetchApplications(viewingJob.id);
    else { setApplications([]); setSelectedTab('details'); }
  }, [viewingJob]);

  const fetchLogo = async () => {
    try {
      setLogoLoading(true);
      setLogoUrl('https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=200&h=80&fit=crop&auto=format');
      const { data } = await supabase.from('settings').select('value').eq('key', 'logo_url').single();
      if (data?.value) {
        const img = new Image();
        img.onload = () => { setLogoUrl(data.value); setLogoLoading(false); };
        img.onerror = () => setLogoLoading(false);
        img.src = data.value;
      } else setLogoLoading(false);
    } catch { setLogoLoading(false); }
  };

  const fetchClientProfile = async () => {
    if (!user) return;
    const { data } = await supabase
      .from('workday_client_profiles')
      .select('project_requirements, background_image_url, company_profile_image_url, companies(name)')
      .eq('id', user.id)
      .maybeSingle();
    if (data?.project_requirements) setClientProjectRequirements(data.project_requirements);
    if ((data as any)?.companies?.name) setClientCompanyName((data as any).companies.name);
    if (data?.background_image_url) {
      const { data: urlData } = supabase.storage.from('profile-photos').getPublicUrl(data.background_image_url);
      setBackgroundImageUrl(urlData.publicUrl);
    }
    if (data?.company_profile_image_url) {
      const { data: urlData } = supabase.storage.from('profile-photos').getPublicUrl(data.company_profile_image_url);
      setCompanyProfileImageUrl(urlData.publicUrl);
    }
  };

  const fetchPlan = async () => {
    if (!user) return;
    if (adminPlanOverride !== null) {
      // Admin override already set in initial state — no DB call needed
      setPlanLoading(false);
      return;
    }
    const { data } = await supabase
      .from('workday_client_profiles')
      .select('plan, in_grace_period, pro_grace_until, has_pro_job_access')
      .eq('id', user.id)
      .maybeSingle();
    const effectivePro = data?.plan === 'pro'
      || (data?.in_grace_period === true && !!data?.pro_grace_until && new Date(data.pro_grace_until) > new Date())
      || (data?.has_pro_job_access === true && !!data?.pro_grace_until && new Date(data.pro_grace_until) > new Date());
    setIsPro(effectivePro);
    setPlanLoading(false);
  };

  const fetchData = async () => {
    if (!user) return;
    setLoading(true);
    const { data: jobs } = await supabase
      .from('workday_client_job_postings')
      .select('*')
      .eq('customer_id', user.id)
      .order('created_at', { ascending: false });
    const { data: services } = await supabase
      .from('workday_client_service_requirements')
      .select('*')
      .eq('customer_id', user.id)
      .order('created_at', { ascending: false });

    setJobPostings(jobs || []);
    setServiceRequirements(services || []);
    if (jobs && jobs.length > 0) {
      const { data: apps } = await supabase.from('job_applications').select('job_posting_id').in('job_posting_id', jobs.map(j => j.id));
      const counts: Record<string, number> = {};
      apps?.forEach(a => { counts[a.job_posting_id] = (counts[a.job_posting_id] || 0) + 1; });
      setApplicationCounts(counts);
    }
    setLoading(false);
  };

  const fetchApplications = async (jobId: string) => {
    setLoadingApplications(true);
    const { data } = await supabase.from('job_applications').select('*').eq('job_posting_id', jobId).order('applied_at', { ascending: false });
    setApplications(data || []);
    setLoadingApplications(false);
  };

  const updateApplicationStatus = async (appId: string, newStatus: string) => {
    setUpdatingStatus(appId);
    await supabase.from('job_applications').update({ status: newStatus, reviewed_by: user?.id, reviewed_at: new Date().toISOString() }).eq('id', appId);
    setApplications(prev => prev.map(a => a.id === appId ? { ...a, status: newStatus } : a));
    setUpdatingStatus(null);
  };

  const sendPrescreenRequest = async () => {
    if (!user || !viewingJob || !scheduleFor) return;
    const validSlots = scheduleSlots.filter(s => s.date && s.time);
    setSendingPrescreen(scheduleFor.id);
    const { error } = await supabase.from('candidate_messages').insert({
      client_id: user.id,
      consultant_id: scheduleFor.consultant_id,
      sender_type: 'client',
      message_body: `Schedule Pre-Screen Interview — ${viewingJob.job_title}`,
      message_type: 'prescreen_request',
      metadata: {
        application_id: scheduleFor.id,
        job_posting_id: viewingJob.id,
        job_title: viewingJob.job_title,
        company_name: clientCompanyName,
        proposed_slots: validSlots.length > 0 ? validSlots : undefined,
        note: scheduleNote.trim() || undefined,
      },
    });
    if (!error) {
      await supabase.from('job_applications').update({ prescreen_status: 'requested' }).eq('id', scheduleFor.id);
      setApplications(prev => prev.map(a => a.id === scheduleFor!.id ? { ...a, prescreen_status: 'requested' } : a));
      setPrescreenSent(prev => new Set([...prev, scheduleFor!.id]));
      await transitionCandidateStage({
        clientId: user.id,
        consultantId: scheduleFor.consultant_id,
        jobPostingId: viewingJob.id,
        newStage: 'screening',
        triggeredBy: 'prescreen',
      });
    }
    setSendingPrescreen(null);
    setScheduleFor(null);
    setScheduleSlots([{ date: '', time: '' }, { date: '', time: '' }, { date: '', time: '' }]);
    setScheduleNote('');
  };

  const sendProposeNewTimes = async () => {
    if (!user || !proposeTimesFor || !viewingJob) return;
    const validSlots = proposeSlots.filter(s => s.date && s.time);
    if (validSlots.length === 0) return;
    setSendingPropose(true);
    const companyName = clientCompanyName;
    await supabase.from('candidate_messages').insert({
      client_id: user.id,
      consultant_id: proposeTimesFor.consultant_id,
      sender_type: 'client',
      message_body: `Schedule Pre-Screen Interview — ${viewingJob.job_title}`,
      message_type: 'prescreen_new_times',
      metadata: {
        application_id: proposeTimesFor.id,
        job_posting_id: viewingJob.id,
        job_title: viewingJob.job_title,
        company_name: companyName,
        proposed_slots: validSlots,
        note: proposeNote.trim() || undefined,
      },
    });
    await supabase.from('job_applications').update({ prescreen_status: 'rescheduling' }).eq('id', proposeTimesFor.id);
    setApplications(prev => prev.map(a => a.id === proposeTimesFor.id ? { ...a, prescreen_status: 'rescheduling' } : a));
    setSendingPropose(false);
    setProposeTimesFor(null);
    setProposeSlots([{ date: '', time: '' }, { date: '', time: '' }, { date: '', time: '' }]);
    setProposeNote('');
  };

  const openConfirmModal = async (app: JobApplication) => {
    if (!user) return;
    const { data: msgs } = await supabase
      .from('candidate_messages')
      .select('*')
      .eq('client_id', user.id)
      .eq('consultant_id', app.consultant_id)
      .eq('message_type', 'prescreen_availability')
      .order('created_at', { ascending: false })
      .limit(1);
    const latest = msgs?.[0];
    const slots: Array<{ date: string; time: string }> = latest?.metadata?.slots || [];
    setConfirmFor({ app, slots, msgId: latest?.id || '' });
  };

  const confirmPrescreen = async (slot: { date: string; time: string }) => {
    if (!user || !confirmFor || !viewingJob) return;
    setConfirmingSlot(true);
    const { error } = await supabase.from('candidate_messages').insert({
      client_id: user.id,
      consultant_id: confirmFor.app.consultant_id,
      sender_type: 'client',
      message_body: `Your pre-screen interview for ${viewingJob.job_title} is confirmed!`,
      message_type: 'prescreen_confirmed',
      metadata: {
        application_id: confirmFor.app.id,
        job_posting_id: viewingJob.id,
        job_title: viewingJob.job_title,
        company_name: clientCompanyName,
        confirmed_slot: slot,
      },
    });
    if (!error) {
      await supabase.from('job_applications').update({ prescreen_status: 'confirmed' }).eq('id', confirmFor.app.id);
      setApplications(prev => prev.map(a => a.id === confirmFor!.app.id ? { ...a, prescreen_status: 'confirmed' } : a));
      await transitionCandidateStage({
        clientId: user.id,
        consultantId: confirmFor.app.consultant_id,
        jobPostingId: viewingJob.id,
        newStage: 'interview',
        triggeredBy: 'interview_confirm',
      });
    }
    setConfirmingSlot(false);
    setConfirmFor(null);
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/customer/login');
  };

  const handleJobSubmit = async (status: string = 'open') => {
    if (!user?.id) return;
    if (!jobForm.requisition_number || !jobForm.job_title || !jobForm.hire_deadline_date || !jobForm.job_description || !jobForm.skills_qualifications) {
      setMessage('Please fill in all required fields.');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    if (!editingId) {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (token) {
        const quotaError = await checkPostQuotaServer(
          token,
          import.meta.env.VITE_SUPABASE_URL,
          import.meta.env.VITE_SUPABASE_ANON_KEY,
          jobForm.job_title,
          jobForm.skills_qualifications
        );
        if (quotaError) {
          setMessage(quotaError);
          setTimeout(() => setMessage(''), 6000);
          return;
        }
      }
    }
    setSubmitting(true);
    try {
      const jobData = {
        customer_id: user.id,
        requisition_number: jobForm.requisition_number,
        job_title: jobForm.job_title,
        hire_deadline_date: jobForm.hire_deadline_date,
        worker_type: jobForm.worker_type,
        job_description: jobForm.job_description,
        supervisory_organization: jobForm.supervisory_organization,
        job_family: jobForm.job_family,
        skills_qualifications: jobForm.skills_qualifications,
        compensation_amount: jobForm.compensation_amount ? parseFloat(jobForm.compensation_amount) : null,
        comp_grade: jobForm.comp_grade,
        status,
        is_pro_posting: jobForm.is_pro_posting,
        career_page_url: jobForm.career_page_url.trim() || null,
        application_questions: jobForm.application_questions.filter(q => q.question.trim())
      };
      if (editingId) {
        await supabase.from('workday_client_job_postings').update(jobData).eq('id', editingId);
        setMessage('Requisition updated successfully!');
      } else {
        await supabase.from('workday_client_job_postings').insert([jobData]);
        setMessage('Requisition posted successfully!');
      }
      resetJobForm();
      setActiveView('list');
      setEditingId(null);
      fetchData();
      setTimeout(() => setMessage(''), 5000);
    } catch (err: any) {
      setMessage(`Error: ${err?.message || 'Please try again.'}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleServiceSubmit = async () => {
    if (!user?.id) return;
    setSubmitting(true);
    try {
      const serviceData = {
        customer_id: user.id,
        project_title: serviceForm.project_title,
        position_requirements: serviceForm.position_requirements,
        issues_and_modules: serviceForm.issues_and_modules,
        project_requirements: serviceForm.project_requirements,
        implementation_stages: serviceForm.implementation_stages,
        implementation_partner: serviceForm.implementation_partner,
        number_of_employees: serviceForm.number_of_employees,
        go_live_date: serviceForm.go_live_date || null,
        hire_deadline_date: serviceForm.hire_deadline_date || null,
        enhancements: serviceForm.enhancements
      };
      if (editingId) {
        await supabase.from('workday_client_service_requirements').update(serviceData).eq('id', editingId);
        setMessage('Development requirement updated!');
      } else {
        await supabase.from('workday_client_service_requirements').insert([serviceData]);
        setMessage('Development requirement submitted!');
      }
      resetServiceForm();
      setActiveView('list');
      setEditingId(null);
      fetchData();
      setTimeout(() => setMessage(''), 5000);
    } catch (err: any) {
      setMessage(`Error: ${err?.message || 'Please try again.'}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditJob = (job: JobPosting) => {
    setEditingId(job.id);
    setJobForm({
      requisition_number: job.requisition_number,
      job_title: job.job_title || '',
      hire_deadline_date: job.hire_deadline_date,
      worker_type: job.worker_type,
      job_description: job.job_description,
      supervisory_organization: job.supervisory_organization || '',
      job_family: job.job_family || '',
      skills_qualifications: job.skills_qualifications,
      compensation_amount: job.compensation_amount?.toString() || '',
      comp_grade: job.comp_grade || '',
      status: job.status,
      is_pro_posting: job.is_pro_posting || false,
      career_page_url: job.career_page_url || '',
      application_questions: job.application_questions && job.application_questions.length > 0
        ? job.application_questions
        : [{ question: '' }]
    });
    setPostingType('job');
    setActiveView('create-job');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEditService = (service: ServiceRequirement) => {
    setEditingId(service.id);
    setServiceForm({
      project_title: service.project_title || '',
      position_requirements: service.position_requirements?.length > 0 ? service.position_requirements : [{ positions: '', type: '' }],
      issues_and_modules: service.issues_and_modules || '',
      project_requirements: service.project_requirements || '',
      implementation_stages: service.implementation_stages || '',
      implementation_partner: service.implementation_partner || '',
      number_of_employees: service.number_of_employees || '',
      go_live_date: service.go_live_date || '',
      hire_deadline_date: service.hire_deadline_date || '',
      enhancements: service.enhancements || ''
    });
    setPostingType('service');
    setActiveView('create-service');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteJob = async (id: string) => {
    if (!confirm('Delete this job posting?')) return;
    await supabase.from('workday_client_job_postings').delete().eq('id', id);
    setMessage('Job posting deleted.');
    fetchData();
    setTimeout(() => setMessage(''), 3000);
  };

  const handleDeleteService = async (id: string) => {
    if (!confirm('Delete this development requirement?')) return;
    await supabase.from('workday_client_service_requirements').delete().eq('id', id);
    setMessage('Development requirement deleted.');
    fetchData();
    setTimeout(() => setMessage(''), 3000);
  };

  const handleUploadBrandImage = async (file: File, type: 'background' | 'profile') => {
    if (!user) return;
    const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    // Fixed filename per type so re-uploads always replace the same object (avoids accumulating files)
    const path = `client-brand/${user.id}/${type}.${ext}`;
    if (type === 'background') setUploadingBackground(true);
    else setUploadingProfile(true);
    try {
      // Remove the old file first (DELETE policy allows this), then INSERT fresh
      await supabase.storage.from('profile-photos').remove([
        `client-brand/${user.id}/background.jpg`,
        `client-brand/${user.id}/background.jpeg`,
        `client-brand/${user.id}/background.png`,
        `client-brand/${user.id}/background.webp`,
        `client-brand/${user.id}/background.gif`,
        `client-brand/${user.id}/profile.jpg`,
        `client-brand/${user.id}/profile.jpeg`,
        `client-brand/${user.id}/profile.png`,
        `client-brand/${user.id}/profile.webp`,
        `client-brand/${user.id}/profile.gif`,
      ].filter(p => p.startsWith(`client-brand/${user.id}/${type}.`)));

      const { error: uploadError } = await supabase.storage
        .from('profile-photos')
        .upload(path, file, { upsert: true, cacheControl: '3600' });
      if (uploadError) throw uploadError;

      const column = type === 'background' ? 'background_image_url' : 'company_profile_image_url';
      const { error: dbError } = await supabase
        .from('workday_client_profiles')
        .update({ [column]: path })
        .eq('id', user.id);
      if (dbError) throw dbError;

      const { data: urlData } = supabase.storage.from('profile-photos').getPublicUrl(path);
      // Append cache-buster so browser shows the new image immediately
      const freshUrl = `${urlData.publicUrl}?t=${Date.now()}`;
      if (type === 'background') setBackgroundImageUrl(freshUrl);
      else setCompanyProfileImageUrl(freshUrl);
      setMessage(`${type === 'background' ? 'Cover photo' : 'Logo'} updated successfully!`);
      setTimeout(() => setMessage(''), 4000);
    } catch (err: any) {
      setMessage(`Upload failed: ${err?.message || 'Please try again.'}`);
      setTimeout(() => setMessage(''), 5000);
    } finally {
      if (type === 'background') setUploadingBackground(false);
      else setUploadingProfile(false);
    }
  };

  const handleQuickPostSubmit = async () => {
    if (!user?.id) return;
    const { requisition_number, job_title, hire_deadline_date, career_page_url } = quickPostForm;
    if (!requisition_number || !job_title || !hire_deadline_date || !career_page_url) {
      setMessage('Please fill in all required fields including the Workday Requisition URL.');
      setTimeout(() => setMessage(''), 4000);
      return;
    }
    {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (token) {
        const quotaError = await checkPostQuotaServer(
          token,
          import.meta.env.VITE_SUPABASE_URL,
          import.meta.env.VITE_SUPABASE_ANON_KEY,
          quickPostForm.job_title,
          ''
        );
        if (quotaError) {
          setMessage(quotaError);
          setTimeout(() => setMessage(''), 6000);
          return;
        }
      }
    }
    setSubmitting(true);
    try {
      const { error } = await supabase.from('workday_client_job_postings').insert([{
        customer_id: user.id,
        requisition_number: quickPostForm.requisition_number,
        job_title: quickPostForm.job_title,
        hire_deadline_date: quickPostForm.hire_deadline_date,
        worker_type: quickPostForm.worker_type,
        supervisory_organization: quickPostForm.supervisory_organization || null,
        job_family: quickPostForm.job_family || null,
        compensation_amount: quickPostForm.compensation_amount ? parseFloat(quickPostForm.compensation_amount) : null,
        comp_grade: quickPostForm.comp_grade || null,
        career_page_url: quickPostForm.career_page_url.trim(),
        is_pro_posting: true,
        status: 'open',
        job_description: '',
        skills_qualifications: '',
        application_questions: [],
      }]);
      if (error) throw error;
      setMessage('Quick Post published! Consultants can now view and apply via your Workday link.');
      setQuickPostForm({ requisition_number: '', job_title: '', hire_deadline_date: '', worker_type: 'hire', supervisory_organization: '', job_family: '', compensation_amount: '', comp_grade: '', career_page_url: '' });
      setActiveView('list');
      fetchData();
      setTimeout(() => setMessage(''), 6000);
    } catch (err: any) {
      setMessage(`Error: ${err?.message || 'Please try again.'}`);
      setTimeout(() => setMessage(''), 6000);
    } finally {
      setSubmitting(false);
    }
  };

  const resetPipelinePostForm = () => {
    setPipelinePostForm({ requisition_number: '', job_title: '', hire_deadline_date: '', worker_type: 'hire', job_description: '', supervisory_organization: '', job_family: '', skills_qualifications: '', compensation_amount: '', comp_grade: '', status: 'open', application_questions: [{ question: '' }] });
  };

  const handlePipelinePostSubmit = async () => {
    if (!user?.id) return;
    setPipelineFormError('');
    const { requisition_number, job_title, hire_deadline_date, job_description, skills_qualifications } = pipelinePostForm;
    if (!requisition_number || !job_title || !hire_deadline_date || !job_description || !skills_qualifications) {
      setPipelineFormError('Please fill in all required fields: Requisition #, Job Title, Hire Deadline, Job Description, and Skills & Qualifications.');
      return;
    }
    {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (token) {
        const quotaError = await checkPostQuotaServer(
          token,
          import.meta.env.VITE_SUPABASE_URL,
          import.meta.env.VITE_SUPABASE_ANON_KEY,
          pipelinePostForm.job_title,
          pipelinePostForm.skills_qualifications
        );
        if (quotaError) {
          setPipelineFormError(quotaError);
          return;
        }
      }
    }
    setSubmitting(true);
    try {
      const validQuestions = pipelinePostForm.application_questions.filter(q => q.question.trim());
      const { error } = await supabase.from('workday_client_job_postings').insert([{
        customer_id: user.id,
        requisition_number: pipelinePostForm.requisition_number,
        job_title: pipelinePostForm.job_title,
        hire_deadline_date: pipelinePostForm.hire_deadline_date,
        worker_type: pipelinePostForm.worker_type,
        job_description: pipelinePostForm.job_description,
        supervisory_organization: pipelinePostForm.supervisory_organization || null,
        job_family: pipelinePostForm.job_family || null,
        skills_qualifications: pipelinePostForm.skills_qualifications,
        compensation_amount: pipelinePostForm.compensation_amount ? parseFloat(pipelinePostForm.compensation_amount) : null,
        comp_grade: pipelinePostForm.comp_grade || null,
        is_pro_posting: true,
        status: pipelinePostForm.status,
        career_page_url: null,
        application_questions: validQuestions.length > 0 ? validQuestions : [],
      }]);
      if (error) throw error;
      setMessage('Pipeline posting published! Consultants can apply and be managed through the full PerplexityATS pipeline.');
      resetPipelinePostForm();
      setActiveView('list');
      fetchData();
      setTimeout(() => setMessage(''), 6000);
    } catch (err: any) {
      setPipelineFormError(`Failed to publish: ${err?.message || 'Please try again.'}`);
    } finally {
      setSubmitting(false);
    }
  };

  const resetJobForm = () => {
    setJobForm({ requisition_number: '', job_title: '', hire_deadline_date: '', worker_type: 'hire', job_description: '', supervisory_organization: '', job_family: '', skills_qualifications: '', compensation_amount: '', comp_grade: '', status: 'open', is_pro_posting: false, career_page_url: '', application_questions: [{ question: '' }] });
  };

  const resetServiceForm = () => {
    setServiceForm({ project_title: '', position_requirements: [{ positions: '', type: '' }], issues_and_modules: '', project_requirements: clientProjectRequirements, implementation_stages: '', implementation_partner: '', number_of_employees: '', go_live_date: '', hire_deadline_date: '', enhancements: '' });
  };

  const startNewJob = () => {
    setEditingId(null);
    resetJobForm();
    setPostingType('job');
    setActiveView('create-job');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const startNewService = () => {
    setEditingId(null);
    resetServiceForm();
    setPostingType('service');
    setActiveView('create-service');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getFilteredJobs = () => {
    let items = [...jobPostings];
    if (jobSearchTerm) {
      const q = jobSearchTerm.toLowerCase();
      items = items.filter(j => j.requisition_number?.toLowerCase().includes(q) || j.job_title?.toLowerCase().includes(q) || j.job_description?.toLowerCase().includes(q));
    }
    if (jobFilterStatus !== 'all') items = items.filter(j => j.status === jobFilterStatus);
    if (jobSortBy === 'date') items.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    else if (jobSortBy === 'deadline') items.sort((a, b) => new Date(a.hire_deadline_date).getTime() - new Date(b.hire_deadline_date).getTime());
    else items.sort((a, b) => a.status.localeCompare(b.status));
    return items;
  };

  const getPerplexityJobs = () => getFilteredJobs().filter(j => !j.is_pro_posting);
  const getProDirectJobs = () => getFilteredJobs().filter(j => j.is_pro_posting);

  const getFilteredServices = () => {
    let items = [...serviceRequirements];
    if (serviceSearchTerm) {
      const q = serviceSearchTerm.toLowerCase();
      items = items.filter(s => s.project_title?.toLowerCase().includes(q) || s.issues_and_modules?.toLowerCase().includes(q) || s.project_requirements?.toLowerCase().includes(q));
    }
    if (serviceSortBy === 'date') items.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    else items.sort((a, b) => new Date(a.hire_deadline_date || 0).getTime() - new Date(b.hire_deadline_date || 0).getTime());
    return items;
  };

  const card = isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300';
  const inputCls = isDark
    ? 'bg-gray-700 border-gray-500 text-white placeholder-gray-400'
    : 'bg-white border-gray-400 text-gray-900 placeholder-gray-400';
  const labelCls = isDark ? 'text-gray-300' : 'text-gray-700';
  const subLabelCls = isDark ? 'text-gray-400' : 'text-gray-500';

  const filteredJobs = getFilteredJobs();
  const perplexityJobs = getPerplexityJobs();
  const proDirectJobs = getProDirectJobs();
  const filteredServices = getFilteredServices();

  return (
    <div className={`min-h-screen transition-colors duration-200 ${isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <WorkdayClientHeader />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-32 pb-16">
        <div className="flex items-center mb-8 gap-3">
          <button onClick={() => navigate('/workday-client/dashboard')} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-800 text-gray-400 hover:text-white' : 'hover:bg-gray-200 text-gray-500 hover:text-gray-900'}`}>
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold">Post Jobs & Workday Needs</h1>
            <p className={`text-sm ${subLabelCls}`}>Manage your job requisitions and Workday development requests</p>
          </div>
        </div>

        {message && (
          <div className={`flex items-center gap-3 p-4 rounded-xl mb-6 text-sm font-medium ${message.includes('Error') ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'}`}>
            {message.includes('Error') ? <AlertCircle className="w-5 h-5 flex-shrink-0" /> : <CheckCircle className="w-5 h-5 flex-shrink-0" />}
            {message}
          </div>
        )}

        {activeView === 'list' && (
          <>
            {/* LinkedIn-style Company Brand Header */}
            <div className={`rounded-2xl border overflow-hidden mb-8 ${card}`}>
              {/* Background banner — 1584×191 aspect ratio */}
              <div className="relative w-full group overflow-hidden" style={{ paddingTop: 'calc(191 / 1584 * 100%)' }}>
                <div className="absolute inset-0">
                  {backgroundImageUrl ? (
                    <img src={backgroundImageUrl} alt="Company background" className="w-full h-full object-cover" />
                  ) : (
                    <div className={`w-full h-full ${isDark ? 'bg-gradient-to-br from-gray-700 to-gray-800' : 'bg-gradient-to-br from-slate-200 to-slate-300'}`} />
                  )}
                  <button
                    onClick={() => backgroundInputRef.current?.click()}
                    disabled={uploadingBackground}
                    className="absolute top-2 right-3 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-black/50 text-white text-xs font-medium hover:bg-black/70 transition-colors opacity-0 group-hover:opacity-100"
                  >
                    {uploadingBackground ? <div className="animate-spin rounded-full h-3 w-3 border-t border-white" /> : <Camera className="w-3.5 h-3.5" />}
                    {uploadingBackground ? 'Uploading...' : 'Change Cover'}
                  </button>
                  <input ref={backgroundInputRef} type="file" accept="image/*" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) handleUploadBrandImage(f, 'background'); e.target.value = ''; }} />
                </div>
              </div>

              {/* Profile image + company info row */}
              <div className="px-6 pb-5">
                <div className="flex items-end gap-4 -mt-8 mb-4">
                  <div className="relative group flex-shrink-0">
                    <div className={`w-20 h-20 sm:w-24 sm:h-24 rounded-xl border-4 overflow-hidden ${isDark ? 'border-gray-800 bg-gray-700' : 'border-white bg-gray-100'} shadow-md`}>
                      {companyProfileImageUrl ? (
                        <img src={companyProfileImageUrl} alt="Company profile" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Building2 className={`w-8 h-8 sm:w-10 sm:h-10 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => profileInputRef.current?.click()}
                      disabled={uploadingProfile}
                      className="absolute inset-0 rounded-xl flex items-center justify-center bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      {uploadingProfile ? <div className="animate-spin rounded-full h-4 w-4 border-t border-white" /> : <Camera className="w-5 h-5" />}
                    </button>
                    <input ref={profileInputRef} type="file" accept="image/*" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) handleUploadBrandImage(f, 'profile'); e.target.value = ''; }} />
                  </div>
                  <div className="pb-1">
                    <p className={`text-xs ${subLabelCls}`}>Upload your company logo and cover photo to brand your job postings</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <button onClick={() => backgroundInputRef.current?.click()} className={`flex items-center gap-1.5 px-3 py-1 rounded-lg border text-xs font-medium transition-colors ${isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                        <ImageIcon className="w-3.5 h-3.5" />Cover Photo
                      </button>
                      <button onClick={() => profileInputRef.current?.click()} className={`flex items-center gap-1.5 px-3 py-1 rounded-lg border text-xs font-medium transition-colors ${isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                        <Upload className="w-3.5 h-3.5" />Logo
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action buttons grid */}
            <div className={`grid gap-4 mb-8 ${isPro ? 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-4' : 'grid-cols-2'}`}>
              <button
                onClick={() => setShowPostRequisitionPopup(true)}
                className="group relative overflow-hidden rounded-xl border-2 border-dashed border-[#40E0D0]/40 p-6 text-left hover:border-[#40E0D0] hover:bg-[#40E0D0]/5 transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#40E0D0]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#40E0D0]/20 transition-colors">
                    <Briefcase className="w-6 h-6 text-[#40E0D0]" />
                  </div>
                  <div>
                    <p className="font-semibold text-base mb-1">Posting a Workday Requisition?</p>
                    <p className={`text-sm ${subLabelCls}`}>Through Perplexity Team</p>
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                    {jobPostings.length} posting{jobPostings.length !== 1 ? 's' : ''}
                  </span>
                  <Plus className="w-4 h-4 text-[#40E0D0]" />
                </div>
              </button>

              {/* Quick Post via Workday Link — Pro only */}
              {planLoading ? (
                <div className={`rounded-xl border-2 border-dashed p-6 animate-pulse ${isDark ? 'border-gray-700 bg-gray-800/30' : 'border-gray-200 bg-gray-50'}`}>
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex-shrink-0 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />
                    <div className="flex-1 space-y-2 pt-1">
                      <div className={`h-4 rounded w-3/4 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />
                      <div className={`h-3 rounded w-full ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />
                    </div>
                  </div>
                </div>
              ) : isPro ? (
                <button
                  onClick={() => setActiveView('quick-post')}
                  className="group relative overflow-hidden rounded-xl border-2 border-[#40E0D0]/40 bg-gradient-to-br from-[#40E0D0]/5 to-[#40E0D0]/10 p-6 text-left hover:border-[#40E0D0] hover:from-[#40E0D0]/10 hover:to-[#40E0D0]/15 transition-all"
                >
                  <div className="absolute top-3 right-3">
                    <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#40E0D0] text-black text-xs font-bold">
                      <Star className="w-3 h-3" />Pro
                    </span>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#40E0D0]/20 flex items-center justify-center flex-shrink-0 group-hover:bg-[#40E0D0]/30 transition-colors">
                      <Zap className="w-6 h-6 text-[#40E0D0]" />
                    </div>
                    <div>
                      <p className="font-semibold text-base mb-1">Quick Post via Workday Link</p>
                      <p className={`text-sm ${subLabelCls}`}>Paste your Workday requisition URL — skip the full form</p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <span className="text-xs text-[#40E0D0] font-medium">Exclusive to Pro members →</span>
                  </div>
                </button>
              ) : (
                <div className={`rounded-xl border-2 border-dashed p-6 ${isDark ? 'border-gray-700 bg-gray-800/30' : 'border-gray-200 bg-gray-50'}`}>
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <Zap className="w-6 h-6 text-gray-400" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <p className={`font-semibold text-base ${isDark ? 'text-gray-400' : 'text-gray-400'}`}>Quick Post via Workday Link</p>
                        <Lock className="w-3.5 h-3.5 text-gray-400" />
                      </div>
                      <p className={`text-sm ${subLabelCls}`}>Paste your Workday URL instead of filling out the full form. Pro plan only.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Pipeline Post — Pro only */}
              {planLoading ? (
                <div className={`rounded-xl border-2 border-dashed p-6 animate-pulse ${isDark ? 'border-gray-700 bg-gray-800/30' : 'border-gray-200 bg-gray-50'}`}>
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex-shrink-0 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />
                    <div className="flex-1 space-y-2 pt-1">
                      <div className={`h-4 rounded w-3/4 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />
                      <div className={`h-3 rounded w-full ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />
                    </div>
                  </div>
                </div>
              ) : isPro ? (
                <button
                  onClick={() => setActiveView('pipeline-post')}
                  className="group relative overflow-hidden rounded-xl border-2 border-[#40E0D0]/40 bg-gradient-to-br from-[#40E0D0]/5 to-[#40E0D0]/10 p-6 text-left hover:border-[#40E0D0] hover:from-[#40E0D0]/10 hover:to-[#40E0D0]/15 transition-all"
                >
                  <div className="absolute top-3 right-3">
                    <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#40E0D0] text-black text-xs font-bold">
                      <Star className="w-3 h-3" />Pro
                    </span>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#40E0D0]/20 flex items-center justify-center flex-shrink-0 group-hover:bg-[#40E0D0]/30 transition-colors">
                      <GitMerge className="w-6 h-6 text-[#40E0D0]" />
                    </div>
                    <div>
                      <p className="font-semibold text-base mb-1">Job Post via Pipeline & Tracking</p>
                      <p className={`text-sm ${subLabelCls}`}>Uses PerplexityATS Interview to Offer</p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <span className="text-xs text-[#40E0D0] font-medium">Full managed hiring pipeline →</span>
                  </div>
                </button>
              ) : (
                <div className={`rounded-xl border-2 border-dashed p-6 ${isDark ? 'border-gray-700 bg-gray-800/30' : 'border-gray-200 bg-gray-50'}`}>
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <GitMerge className="w-6 h-6 text-gray-400" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <p className={`font-semibold text-base ${isDark ? 'text-gray-400' : 'text-gray-400'}`}>Job Post via Pipeline & Tracking</p>
                        <Lock className="w-3.5 h-3.5 text-gray-400" />
                      </div>
                      <p className={`text-sm ${subLabelCls}`}>PerplexityATS Interview to Offer managed pipeline. Pro plan only.</p>
                    </div>
                  </div>
                </div>
              )}

              <button
                onClick={startNewService}
                className="group relative overflow-hidden rounded-xl border-2 border-dashed border-[#40E0D0]/40 p-6 text-left hover:border-[#40E0D0] hover:bg-[#40E0D0]/5 transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#40E0D0]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#40E0D0]/20 transition-colors">
                    <Layers className="w-6 h-6 text-[#40E0D0]" />
                  </div>
                  <div>
                    <p className="font-semibold text-base mb-1">Workday Development Request</p>
                    <p className={`text-sm ${subLabelCls}`}>Implementation, configuration & enhancement projects</p>
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                    {serviceRequirements.length} request{serviceRequirements.length !== 1 ? 's' : ''}
                  </span>
                  <Plus className="w-4 h-4 text-[#40E0D0]" />
                </div>
              </button>
            </div>

            {/* ── WORKDAY JOB POSTINGS — Through Perplexity ── */}
            <div className={`rounded-xl border ${card} mb-6`}>
              <div className={`px-6 py-5 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-[#40E0D0]" />
                    <h2 className="font-semibold text-lg">Workday Job Postings</h2>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>{perplexityJobs.length}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isDark ? 'bg-teal-900/40 text-teal-400 border border-teal-700' : 'bg-teal-50 text-teal-700 border border-teal-200'}`}>Through Perplexity</span>
                  </div>
                </div>
                <p className={`text-xs mb-4 ${subLabelCls}`}>Full-form requisitions submitted through Perplexity. Our team manages placement and candidate matching.</p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1 relative">
                    <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${subLabelCls}`} />
                    <input type="text" placeholder="Search requisitions..." value={jobSearchTerm} onChange={e => setJobSearchTerm(e.target.value)} className={`w-full pl-9 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#40E0D0] ${inputCls}`} />
                  </div>
                  <select value={jobSortBy} onChange={e => setJobSortBy(e.target.value as any)} className={`px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#40E0D0] ${inputCls}`}>
                    <option value="date">Newest first</option>
                    <option value="deadline">Deadline</option>
                    <option value="status">Status</option>
                  </select>
                  <select value={jobFilterStatus} onChange={e => setJobFilterStatus(e.target.value as any)} className={`px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#40E0D0] ${inputCls}`}>
                    <option value="all">All statuses</option>
                    <option value="draft">Draft</option>
                    <option value="open">Open</option>
                    <option value="active">Active</option>
                    <option value="filled">Filled</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
              </div>

              {loading ? (
                <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#40E0D0]"></div></div>
              ) : perplexityJobs.length === 0 ? (
                <div className="py-16 text-center">
                  <Briefcase className={`w-10 h-10 mx-auto mb-3 ${subLabelCls} opacity-40`} />
                  <p className={subLabelCls}>{jobSearchTerm ? 'No requisitions match your search.' : 'No Workday job postings through Perplexity yet.'}</p>
                  {!jobSearchTerm && <button onClick={startNewJob} className="mt-3 text-[#40E0D0] text-sm hover:underline">Post your first requisition</button>}
                </div>
              ) : (
                <div className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-100'}`}>
                  {perplexityJobs.map(job => (
                    <div key={job.id} className={`flex items-center gap-4 px-6 py-4 hover:${isDark ? 'bg-gray-700/40' : 'bg-gray-50'} transition-colors`}>
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                        <Briefcase className="w-5 h-5 text-[#40E0D0]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-sm truncate">{job.job_title || job.requisition_number}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[job.status] || 'bg-gray-100 text-gray-700'}`}>{job.status}</span>
                          {(applicationCounts[job.id] || 0) > 0 && (
                            <button onClick={() => { setViewingJob(job); setSelectedTab('applications'); }} className="text-xs px-2 py-0.5 rounded-full bg-[#40E0D0] text-black font-semibold hover:bg-[#40E0D0]/80 transition-colors">
                              {applicationCounts[job.id]} applicant{applicationCounts[job.id] !== 1 ? 's' : ''}
                            </button>
                          )}
                        </div>
                        <div className={`flex items-center gap-3 text-xs mt-1 ${subLabelCls}`}>
                          <span>Req #{job.requisition_number}</span>
                          <span>·</span>
                          <span className="capitalize">{job.worker_type === 'hire' ? 'Full-Time' : 'Contract'}</span>
                          <span>·</span>
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(job.hire_deadline_date).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className="relative flex-shrink-0">
                        <button onClick={() => setOpenDropdown(openDropdown === job.id ? null : job.id)} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}>
                          <MoreVertical className="w-4 h-4" />
                        </button>
                        {openDropdown === job.id && (
                          <div className={`absolute right-0 top-full mt-1 w-44 rounded-xl shadow-xl z-50 border overflow-hidden ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'}`}>
                            <button onClick={() => { setViewingJob(job); setOpenDropdown(null); }} className={`w-full text-left px-4 py-2.5 flex items-center gap-2.5 text-sm transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-50 text-gray-700'}`}><Eye className="w-4 h-4" />View Details</button>
                            <button onClick={() => { handleEditJob(job); setOpenDropdown(null); }} className={`w-full text-left px-4 py-2.5 flex items-center gap-2.5 text-sm transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-50 text-gray-700'}`}><Pencil className="w-4 h-4" />Edit</button>
                            <button onClick={() => { handleDeleteJob(job.id); setOpenDropdown(null); }} className="w-full text-left px-4 py-2.5 flex items-center gap-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"><Trash2 className="w-4 h-4" />Delete</button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── PRO JOB POSTINGS — Full Access (Quick Post via Workday Link) ── */}
            {(isPro || proDirectJobs.length > 0) && (
              <div className={`rounded-xl border-2 ${isDark ? 'border-[#40E0D0]/30 bg-gray-800/50' : 'border-[#40E0D0]/25 bg-teal-50/30'} mb-6`}>
                <div className={`px-6 py-5 border-b ${isDark ? 'border-[#40E0D0]/20' : 'border-[#40E0D0]/15'}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Zap className="w-5 h-5 text-[#40E0D0]" />
                      <h2 className="font-semibold text-lg">Pro Job Postings</h2>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>{proDirectJobs.length}</span>
                      <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#40E0D0] text-black font-bold"><Star className="w-3 h-3" />Full Access</span>
                    </div>
                  </div>
                  <p className={`text-xs mb-4 ${subLabelCls}`}>Quick posts linked directly to your Workday portal. Consultants apply through your company's Workday system.</p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1 relative">
                      <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${subLabelCls}`} />
                      <input type="text" placeholder="Search pro postings..." value={jobSearchTerm} onChange={e => setJobSearchTerm(e.target.value)} className={`w-full pl-9 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#40E0D0] ${inputCls}`} />
                    </div>
                    <select value={jobSortBy} onChange={e => setJobSortBy(e.target.value as any)} className={`px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#40E0D0] ${inputCls}`}>
                      <option value="date">Newest first</option>
                      <option value="deadline">Deadline</option>
                      <option value="status">Status</option>
                    </select>
                    <select value={jobFilterStatus} onChange={e => setJobFilterStatus(e.target.value as any)} className={`px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#40E0D0] ${inputCls}`}>
                      <option value="all">All statuses</option>
                      <option value="draft">Draft</option>
                      <option value="open">Open</option>
                      <option value="active">Active</option>
                      <option value="filled">Filled</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                </div>

                {loading ? (
                  <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#40E0D0]"></div></div>
                ) : proDirectJobs.length === 0 ? (
                  <div className="py-16 text-center">
                    <Zap className={`w-10 h-10 mx-auto mb-3 ${subLabelCls} opacity-40`} />
                    <p className={subLabelCls}>{jobSearchTerm ? 'No pro postings match your search.' : 'No Pro direct postings yet.'}</p>
                    {!jobSearchTerm && <button onClick={() => setActiveView('quick-post')} className="mt-3 text-[#40E0D0] text-sm hover:underline">Create a Quick Post</button>}
                  </div>
                ) : (
                  <div className={`divide-y ${isDark ? 'divide-[#40E0D0]/10' : 'divide-[#40E0D0]/10'}`}>
                    {proDirectJobs.map(job => (
                      <div key={job.id} className={`flex items-center gap-4 px-6 py-4 transition-colors ${isDark ? 'hover:bg-[#40E0D0]/5' : 'hover:bg-[#40E0D0]/5'}`}>
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-[#40E0D0]/20">
                          <Zap className="w-5 h-5 text-[#40E0D0]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-sm truncate">{job.job_title || job.requisition_number}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[job.status] || 'bg-gray-100 text-gray-700'}`}>{job.status}</span>
                            {job.career_page_url && (
                              <a href={job.career_page_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-[#40E0D0] hover:underline">
                                <Link2 className="w-3 h-3" />Workday Link
                              </a>
                            )}
                            {(applicationCounts[job.id] || 0) > 0 && (
                              <button onClick={() => { setViewingJob(job); setSelectedTab('applications'); }} className="text-xs px-2 py-0.5 rounded-full bg-[#40E0D0] text-black font-semibold hover:bg-[#40E0D0]/80 transition-colors">
                                {applicationCounts[job.id]} applicant{applicationCounts[job.id] !== 1 ? 's' : ''}
                              </button>
                            )}
                          </div>
                          <div className={`flex items-center gap-3 text-xs mt-1 ${subLabelCls}`}>
                            <span>Req #{job.requisition_number}</span>
                            <span>·</span>
                            <span className="capitalize">{job.worker_type === 'hire' ? 'Full-Time' : 'Contract'}</span>
                            <span>·</span>
                            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(job.hire_deadline_date).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <div className="relative flex-shrink-0">
                          <button onClick={() => setOpenDropdown(openDropdown === job.id ? null : job.id)} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}>
                            <MoreVertical className="w-4 h-4" />
                          </button>
                          {openDropdown === job.id && (
                            <div className={`absolute right-0 top-full mt-1 w-44 rounded-xl shadow-xl z-50 border overflow-hidden ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'}`}>
                              <button onClick={() => { setViewingJob(job); setOpenDropdown(null); }} className={`w-full text-left px-4 py-2.5 flex items-center gap-2.5 text-sm transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-50 text-gray-700'}`}><Eye className="w-4 h-4" />View Details</button>
                              <button onClick={() => { handleEditJob(job); setOpenDropdown(null); }} className={`w-full text-left px-4 py-2.5 flex items-center gap-2.5 text-sm transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-50 text-gray-700'}`}><Pencil className="w-4 h-4" />Edit</button>
                              <button onClick={() => { handleDeleteJob(job.id); setOpenDropdown(null); }} className="w-full text-left px-4 py-2.5 flex items-center gap-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"><Trash2 className="w-4 h-4" />Delete</button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className={`rounded-xl border ${card}`}>
              <div className="px-6 py-5 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Layers className="w-5 h-5 text-[#40E0D0]" />
                    <h2 className="font-semibold text-lg">Development Requests</h2>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>{serviceRequirements.length}</span>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1 relative">
                    <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${subLabelCls}`} />
                    <input type="text" placeholder="Search development requests..." value={serviceSearchTerm} onChange={e => setServiceSearchTerm(e.target.value)} className={`w-full pl-9 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#40E0D0] ${inputCls}`} />
                  </div>
                  <select value={serviceSortBy} onChange={e => setServiceSortBy(e.target.value as any)} className={`px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#40E0D0] ${inputCls}`}>
                    <option value="date">Newest first</option>
                    <option value="deadline">Deadline</option>
                  </select>
                </div>
              </div>

              {loading ? (
                <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#40E0D0]"></div></div>
              ) : filteredServices.length === 0 ? (
                <div className="py-16 text-center">
                  <Layers className={`w-10 h-10 mx-auto mb-3 ${subLabelCls} opacity-40`} />
                  <p className={subLabelCls}>{serviceSearchTerm ? 'No requests match your search.' : 'No development requests yet.'}</p>
                  {!serviceSearchTerm && <button onClick={startNewService} className="mt-3 text-[#40E0D0] text-sm hover:underline">Submit your first request</button>}
                </div>
              ) : (
                <div className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-100'}`}>
                  {filteredServices.map(service => (
                    <div key={service.id} className={`flex items-center gap-4 px-6 py-4 hover:${isDark ? 'bg-gray-700/40' : 'bg-gray-50'} transition-colors`}>
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                        <Layers className="w-5 h-5 text-[#40E0D0]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm truncate">{service.project_title || 'Development Requirement'}</p>
                        <div className={`flex items-center gap-3 text-xs mt-1 ${subLabelCls}`}>
                          {service.number_of_employees && <span className="flex items-center gap-1"><Users className="w-3 h-3" />{service.number_of_employees}</span>}
                          {service.go_live_date && <><span>·</span><span className="flex items-center gap-1"><Calendar className="w-3 h-3" />Go-live {new Date(service.go_live_date).toLocaleDateString()}</span></>}
                          {service.implementation_partner && <><span>·</span><span>{service.implementation_partner}</span></>}
                        </div>
                      </div>
                      <div className="relative flex-shrink-0">
                        <button onClick={() => setOpenDropdown(openDropdown === `s-${service.id}` ? null : `s-${service.id}`)} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}>
                          <MoreVertical className="w-4 h-4" />
                        </button>
                        {openDropdown === `s-${service.id}` && (
                          <div className={`absolute right-0 top-full mt-1 w-44 rounded-xl shadow-xl z-50 border overflow-hidden ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'}`}>
                            <button onClick={() => { setViewingService(service); setOpenDropdown(null); }} className={`w-full text-left px-4 py-2.5 flex items-center gap-2.5 text-sm transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-50 text-gray-700'}`}><Eye className="w-4 h-4" />View Details</button>
                            <button onClick={() => { handleEditService(service); setOpenDropdown(null); }} className={`w-full text-left px-4 py-2.5 flex items-center gap-2.5 text-sm transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-50 text-gray-700'}`}><Pencil className="w-4 h-4" />Edit</button>
                            <button onClick={() => { handleDeleteService(service.id); setOpenDropdown(null); }} className="w-full text-left px-4 py-2.5 flex items-center gap-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"><Trash2 className="w-4 h-4" />Delete</button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {(activeView === 'create-job') && (
          <div className={`rounded-xl border ${card} overflow-hidden`}>
            <div className={`px-6 py-5 border-b ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-100 bg-gray-50'}`}>
              <div className="flex items-center gap-3 mb-1">
                <button onClick={() => { setActiveView('list'); setEditingId(null); }} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}><ArrowLeft className="w-4 h-4" /></button>
                <Briefcase className="w-5 h-5 text-[#40E0D0]" />
                <h2 className="font-bold text-lg">{editingId ? 'Edit Requisition' : 'Hire through Perplexity'}</h2>
              </div>
              <p className={`text-sm ml-10 ${subLabelCls}`}>Submit your Workday hiring details. Our team will match, screen, and present qualified professionals for your role.</p>
            </div>

            <div className="p-6 space-y-8">
              <div>
                <h3 className={`text-xs font-semibold uppercase tracking-widest mb-4 ${subLabelCls}`}>Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${labelCls}`}>Workday Requisition # <span className="text-red-500">*</span></label>
                    <input type="text" value={jobForm.requisition_number} onChange={e => setJobForm(p => ({ ...p, requisition_number: e.target.value }))} required placeholder="e.g. WD-2024-001" className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${inputCls}`} />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${labelCls}`}>Job Title <span className="text-red-500">*</span></label>
                    <input type="text" value={jobForm.job_title} onChange={e => setJobForm(p => ({ ...p, job_title: e.target.value }))} required placeholder="e.g. Workday HCM Consultant" className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${inputCls}`} />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${labelCls}`}>Hire Deadline <span className="text-red-500">*</span></label>
                    <input type="date" value={jobForm.hire_deadline_date} onChange={e => setJobForm(p => ({ ...p, hire_deadline_date: e.target.value }))} required className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${inputCls}`} />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${labelCls}`}>Worker Type <span className="text-red-500">*</span></label>
                    <select value={jobForm.worker_type} onChange={e => setJobForm(p => ({ ...p, worker_type: e.target.value }))} className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${inputCls}`}>
                      <option value="hire">Full-Time Hire</option>
                      <option value="contract">Contract</option>
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${labelCls}`}>Supervisory Organization</label>
                    <input type="text" value={jobForm.supervisory_organization} onChange={e => setJobForm(p => ({ ...p, supervisory_organization: e.target.value }))} placeholder="e.g. Finance & Accounting" className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${inputCls}`} />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${labelCls}`}>Job Family</label>
                    <input type="text" value={jobForm.job_family} onChange={e => setJobForm(p => ({ ...p, job_family: e.target.value }))} placeholder="e.g. Technology" className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${inputCls}`} />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${labelCls}`}>Compensation Amount</label>
                    <input type="number" step="0.01" value={jobForm.compensation_amount} onChange={e => setJobForm(p => ({ ...p, compensation_amount: e.target.value }))} placeholder="e.g. 120000" className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${inputCls}`} />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${labelCls}`}>Comp Grade</label>
                    <input type="text" value={jobForm.comp_grade} onChange={e => setJobForm(p => ({ ...p, comp_grade: e.target.value }))} placeholder="e.g. Grade 5" className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${inputCls}`} />
                  </div>
                </div>
              </div>

              <div>
                <h3 className={`text-xs font-semibold uppercase tracking-widest mb-4 ${subLabelCls}`}>Job Details</h3>
                <div className="space-y-5">
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${labelCls}`}>Job Description <span className="text-red-500">*</span></label>
                    <textarea value={jobForm.job_description} onChange={e => setJobForm(p => ({ ...p, job_description: e.target.value }))} required rows={5} placeholder="Describe the role, responsibilities, and expectations..." className={`w-full px-4 py-3 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent resize-none ${inputCls}`} />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${labelCls}`}>Skills & Qualifications <span className="text-red-500">*</span></label>
                    <textarea value={jobForm.skills_qualifications} onChange={e => setJobForm(p => ({ ...p, skills_qualifications: e.target.value }))} required rows={4} placeholder="List required skills, certifications, and experience..." className={`w-full px-4 py-3 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent resize-none ${inputCls}`} />
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className={`text-xs font-semibold uppercase tracking-widest ${subLabelCls}`}>Application Questions</h3>
                    <p className={`text-xs mt-1 ${subLabelCls}`}>Applicants will be asked to answer these questions when applying</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setJobForm(p => ({ ...p, application_questions: [...p.application_questions, { question: '' }] }))}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[#40E0D0] text-black text-sm font-medium rounded-lg hover:bg-[#40E0D0]/90 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add Question
                  </button>
                </div>
                <div className="space-y-3">
                  {jobForm.application_questions.map((q, idx) => (
                    <div key={idx} className={`flex items-start gap-3 p-4 rounded-xl border ${isDark ? 'border-gray-600 bg-gray-700/30' : 'border-gray-300 bg-gray-50'}`}>
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-sm mt-0.5 ${isDark ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>{idx + 1}</div>
                      <input
                        type="text"
                        value={q.question}
                        onChange={e => {
                          const updated = [...jobForm.application_questions];
                          updated[idx] = { question: e.target.value };
                          setJobForm(p => ({ ...p, application_questions: updated }));
                        }}
                        placeholder="e.g. How many years of Workday HCM experience do you have?"
                        className={`flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${inputCls}`}
                      />
                      {jobForm.application_questions.length > 1 && (
                        <button type="button" onClick={() => setJobForm(p => ({ ...p, application_questions: p.application_questions.filter((_, i) => i !== idx) }))} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}>
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>


              <div className={`flex items-center gap-2 p-4 rounded-xl border ${isDark ? 'border-gray-600 bg-gray-700/30' : 'border-gray-300 bg-gray-50'}`}>
                <label className={`text-sm font-medium ${labelCls}`}>Posting Status:</label>
                <select value={jobForm.status} onChange={e => setJobForm(p => ({ ...p, status: e.target.value }))} className={`px-3 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] ${inputCls}`}>
                  <option value="draft">Draft (not visible to consultants)</option>
                  <option value="open">Open (visible and accepting applications)</option>
                </select>
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => handleJobSubmit(jobForm.status)} disabled={submitting} className="flex items-center gap-2 px-6 py-3 bg-[#40E0D0] text-black font-semibold rounded-xl hover:bg-[#40E0D0]/90 transition-colors disabled:opacity-50">
                  <Send className="w-4 h-4" />
                  {submitting ? 'Saving...' : editingId ? 'Update Requisition' : 'Submit to Perplexity'}
                </button>
                <button type="button" onClick={() => { setActiveView('list'); setEditingId(null); }} className={`px-6 py-3 rounded-xl font-medium transition-colors ${isDark ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {activeView === 'create-service' && (
          <div className={`rounded-xl border ${card} overflow-hidden`}>
            <div className={`px-6 py-5 border-b ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-100 bg-gray-50'}`}>
              <div className="flex items-center gap-3 mb-1">
                <button onClick={() => { setActiveView('list'); setEditingId(null); }} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}><ArrowLeft className="w-4 h-4" /></button>
                <Layers className="w-5 h-5 text-[#40E0D0]" />
                <h2 className="font-bold text-lg">{editingId ? 'Edit Development Request' : 'New Workday Development Request'}</h2>
              </div>
              <p className={`text-sm ml-10 ${subLabelCls}`}>Describe your Workday implementation or development needs.</p>
            </div>

            <div className="p-6 space-y-8">
              <div>
                <h3 className={`text-xs font-semibold uppercase tracking-widest mb-4 ${subLabelCls}`}>Project Overview</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="md:col-span-2">
                    <label className={`block text-sm font-medium mb-1.5 ${labelCls}`}>Project Title</label>
                    <input type="text" value={serviceForm.project_title} onChange={e => setServiceForm(p => ({ ...p, project_title: e.target.value }))} placeholder="e.g. Workday HCM Phase 2 Implementation" className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${inputCls}`} />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${labelCls}`}>Number of Employees</label>
                    <select value={serviceForm.number_of_employees} onChange={e => setServiceForm(p => ({ ...p, number_of_employees: e.target.value }))} className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${inputCls}`}>
                      <option value="">Select range</option>
                      <option value="1-50">1–50</option>
                      <option value="51-200">51–200</option>
                      <option value="201-500">201–500</option>
                      <option value="501-1000">501–1,000</option>
                      <option value="1001-5000">1,001–5,000</option>
                      <option value="5000+">5,000+</option>
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${labelCls}`}>Implementation Partner</label>
                    <input type="text" value={serviceForm.implementation_partner} onChange={e => setServiceForm(p => ({ ...p, implementation_partner: e.target.value }))} placeholder="e.g. Accenture, Deloitte" className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${inputCls}`} />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${labelCls}`}>Go-Live Date</label>
                    <input type="date" value={serviceForm.go_live_date} onChange={e => setServiceForm(p => ({ ...p, go_live_date: e.target.value }))} className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${inputCls}`} />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${labelCls}`}>Hire Deadline</label>
                    <input type="date" value={serviceForm.hire_deadline_date} onChange={e => setServiceForm(p => ({ ...p, hire_deadline_date: e.target.value }))} className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${inputCls}`} />
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-xs font-semibold uppercase tracking-widest ${subLabelCls}`}>Position Requirements</h3>
                  <button type="button" onClick={() => setServiceForm(p => ({ ...p, position_requirements: [...p.position_requirements, { positions: '', type: '' }] }))} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#40E0D0] text-black text-sm font-medium rounded-lg hover:bg-[#40E0D0]/90 transition-colors">
                    <Plus className="w-4 h-4" />Add Position
                  </button>
                </div>
                <div className="space-y-3">
                  {serviceForm.position_requirements.map((req, idx) => (
                    <div key={idx} className="flex gap-3 items-start">
                      <input type="text" value={req.positions} onChange={e => { const u = [...serviceForm.position_requirements]; u[idx].positions = e.target.value; setServiceForm(p => ({ ...p, position_requirements: u })); }} placeholder="Estimated # of positions" className={`flex-1 px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${inputCls}`} />
                      <select value={req.type} onChange={e => { const u = [...serviceForm.position_requirements]; u[idx].type = e.target.value; setServiceForm(p => ({ ...p, position_requirements: u })); }} className={`flex-1 px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${inputCls}`}>
                        <option value="">Select type</option>
                        <option value="Direct Hire">Direct Hire</option>
                        <option value="Workday UI Tester">Workday UI Tester</option>
                        <option value="Workday SME Consultant">Workday SME Consultant</option>
                      </select>
                      {serviceForm.position_requirements.length > 1 && (
                        <button type="button" onClick={() => setServiceForm(p => ({ ...p, position_requirements: p.position_requirements.filter((_, i) => i !== idx) }))} className={`p-2.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}><X className="w-4 h-4" /></button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className={`text-xs font-semibold uppercase tracking-widest mb-4 ${subLabelCls}`}>Requirements Detail</h3>
                <div className="space-y-5">
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${labelCls}`}>Workday Issues, Modules & Budgets</label>
                    <textarea value={serviceForm.issues_and_modules} onChange={e => setServiceForm(p => ({ ...p, issues_and_modules: e.target.value }))} rows={4} placeholder="Describe your Workday module needs, known issues, and budget range..." className={`w-full px-4 py-3 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent resize-none ${inputCls}`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1.5">
                      <label className={`block text-sm font-medium ${labelCls}`}>Development Requirement</label>
                      {clientProjectRequirements && !editingId && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-[#40E0D0]/10 text-[#40E0D0] font-medium">Pre-filled from your account</span>
                      )}
                    </div>
                    <textarea value={serviceForm.project_requirements} onChange={e => setServiceForm(p => ({ ...p, project_requirements: e.target.value }))} rows={4} placeholder="Describe your project requirements and timelines..." className={`w-full px-4 py-3 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent resize-none ${inputCls}`} />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${labelCls}`}>Implementation Stages</label>
                    <textarea value={serviceForm.implementation_stages} onChange={e => setServiceForm(p => ({ ...p, implementation_stages: e.target.value }))} rows={3} placeholder="Describe the implementation phases..." className={`w-full px-4 py-3 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent resize-none ${inputCls}`} />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${labelCls}`}>Enhancements & Optimizations</label>
                    <textarea value={serviceForm.enhancements} onChange={e => setServiceForm(p => ({ ...p, enhancements: e.target.value }))} rows={3} placeholder="List any enhancements or optimizations needed..." className={`w-full px-4 py-3 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent resize-none ${inputCls}`} />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={handleServiceSubmit} disabled={submitting} className="flex items-center gap-2 px-6 py-3 bg-[#40E0D0] text-black font-semibold rounded-xl hover:bg-[#40E0D0]/90 transition-colors disabled:opacity-50">
                  <Save className="w-4 h-4" />
                  {submitting ? 'Saving...' : editingId ? 'Update Request' : 'Submit Request'}
                </button>
                <button type="button" onClick={() => { setActiveView('list'); setEditingId(null); }} className={`px-6 py-3 rounded-xl font-medium transition-colors ${isDark ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        {/* ── QUICK POST VIA WORKDAY LINK — Pro only ── */}
        {activeView === 'pipeline-post' && (
          <div className={`rounded-xl border ${card} overflow-hidden`}>
            <div className={`px-6 py-5 border-b ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-100 bg-gray-50'}`}>
              <div className="flex items-center gap-3 mb-1">
                <button onClick={() => { setActiveView('list'); setPipelineFormError(''); }} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}><ArrowLeft className="w-4 h-4" /></button>
                <GitMerge className="w-5 h-5 text-[#40E0D0]" />
                <h2 className="font-bold text-lg">Job Post via Pipeline & Tracking</h2>
                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#40E0D0] text-black text-xs font-bold ml-1"><Star className="w-3 h-3" />Pro</span>
              </div>
              <p className={`text-sm ml-10 ${subLabelCls}`}>Full Workday requisition managed through PerplexityATS Interview to Offer pipeline. Your branding is shown to consultants throughout the process.</p>
            </div>

            {/* Branding banner — identical to Quick Post */}
            <div className={`border-b overflow-hidden ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
              <div className="relative w-full group overflow-hidden" style={{ paddingTop: 'calc(191 / 1584 * 100%)' }}>
                <div className="absolute inset-0">
                  {backgroundImageUrl ? (
                    <img src={backgroundImageUrl} alt="Company background" className="w-full h-full object-cover" />
                  ) : (
                    <div className={`w-full h-full ${isDark ? 'bg-gradient-to-br from-gray-700 to-gray-800' : 'bg-gradient-to-br from-slate-200 to-slate-300'}`} />
                  )}
                  <button
                    onClick={() => backgroundInputRef.current?.click()}
                    disabled={uploadingBackground}
                    className="absolute top-2 right-3 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-black/50 text-white text-xs font-medium hover:bg-black/70 transition-colors opacity-0 group-hover:opacity-100"
                  >
                    {uploadingBackground ? <div className="animate-spin rounded-full h-3 w-3 border-t border-white" /> : <Camera className="w-3.5 h-3.5" />}
                    {uploadingBackground ? 'Uploading...' : 'Change Cover'}
                  </button>
                  <input ref={backgroundInputRef} type="file" accept="image/*" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) handleUploadBrandImage(f, 'background'); e.target.value = ''; }} />
                </div>
              </div>
              <div className="px-6 pb-4">
                <div className="flex items-end gap-4 -mt-8 mb-0">
                  <div className="relative group flex-shrink-0">
                    <div className={`w-20 h-20 sm:w-24 sm:h-24 rounded-xl border-4 overflow-hidden ${isDark ? 'border-gray-800 bg-gray-700' : 'border-white bg-gray-100'} shadow-md`}>
                      {companyProfileImageUrl ? (
                        <img src={companyProfileImageUrl} alt="Company logo" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Building2 className={`w-8 h-8 sm:w-10 sm:h-10 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => profileInputRef.current?.click()}
                      disabled={uploadingProfile}
                      className="absolute inset-0 rounded-xl flex items-center justify-center bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      {uploadingProfile ? <div className="animate-spin rounded-full h-4 w-4 border-t border-white" /> : <Camera className="w-5 h-5" />}
                    </button>
                    <input ref={profileInputRef} type="file" accept="image/*" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) handleUploadBrandImage(f, 'profile'); e.target.value = ''; }} />
                  </div>
                  <div className="pb-1">
                    <p className={`text-xs ${subLabelCls}`}>Your branding appears on this job posting for consultants</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <button onClick={() => backgroundInputRef.current?.click()} className={`flex items-center gap-1.5 px-3 py-1 rounded-lg border text-xs font-medium transition-colors ${isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                        <ImageIcon className="w-3.5 h-3.5" />Cover Photo
                      </button>
                      <button onClick={() => profileInputRef.current?.click()} className={`flex items-center gap-1.5 px-3 py-1 rounded-lg border text-xs font-medium transition-colors ${isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                        <Upload className="w-3.5 h-3.5" />Logo
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-8">
              {/* How it works banner */}
              <div className={`flex items-start gap-4 p-4 rounded-xl border ${isDark ? 'border-teal-800 bg-teal-900/20' : 'border-teal-200 bg-teal-50'}`}>
                <GitMerge className="w-5 h-5 text-[#40E0D0] flex-shrink-0 mt-0.5" />
                <div>
                  <p className={`text-sm font-semibold mb-1 ${isDark ? 'text-teal-300' : 'text-teal-800'}`}>How Pipeline & Tracking Works</p>
                  <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    Fill in your full Workday requisition details. Consultants apply directly through PerplexityATS and are managed through the complete Interview to Offer pipeline — sourcing, screening, interview scheduling, offer, and onboarding — all tracked in your Pipeline & Tracking dashboard.
                  </p>
                </div>
              </div>

              {/* Basic Information */}
              <div>
                <h3 className={`text-xs font-semibold uppercase tracking-widest mb-4 ${subLabelCls}`}>Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${labelCls}`}>Workday Requisition # <span className="text-red-500">*</span></label>
                    <input type="text" value={pipelinePostForm.requisition_number} onChange={e => setPipelinePostForm(p => ({ ...p, requisition_number: e.target.value }))} placeholder="e.g. WD-2024-001" className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${inputCls}`} />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${labelCls}`}>Job Title <span className="text-red-500">*</span></label>
                    <input type="text" value={pipelinePostForm.job_title} onChange={e => setPipelinePostForm(p => ({ ...p, job_title: e.target.value }))} placeholder="e.g. Workday HCM Consultant" className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${inputCls}`} />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${labelCls}`}>Hire Deadline <span className="text-red-500">*</span></label>
                    <input type="date" value={pipelinePostForm.hire_deadline_date} onChange={e => setPipelinePostForm(p => ({ ...p, hire_deadline_date: e.target.value }))} className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${inputCls}`} />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${labelCls}`}>Worker Type <span className="text-red-500">*</span></label>
                    <select value={pipelinePostForm.worker_type} onChange={e => setPipelinePostForm(p => ({ ...p, worker_type: e.target.value }))} className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${inputCls}`}>
                      <option value="hire">Full-Time Hire</option>
                      <option value="contract">Contract</option>
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${labelCls}`}>Supervisory Organization</label>
                    <input type="text" value={pipelinePostForm.supervisory_organization} onChange={e => setPipelinePostForm(p => ({ ...p, supervisory_organization: e.target.value }))} placeholder="e.g. Finance & Accounting" className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${inputCls}`} />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${labelCls}`}>Job Family</label>
                    <input type="text" value={pipelinePostForm.job_family} onChange={e => setPipelinePostForm(p => ({ ...p, job_family: e.target.value }))} placeholder="e.g. Technology" className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${inputCls}`} />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${labelCls}`}>Compensation Amount</label>
                    <input type="number" step="0.01" value={pipelinePostForm.compensation_amount} onChange={e => setPipelinePostForm(p => ({ ...p, compensation_amount: e.target.value }))} placeholder="e.g. 120000" className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${inputCls}`} />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${labelCls}`}>Comp Grade</label>
                    <input type="text" value={pipelinePostForm.comp_grade} onChange={e => setPipelinePostForm(p => ({ ...p, comp_grade: e.target.value }))} placeholder="e.g. Grade 5" className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${inputCls}`} />
                  </div>
                </div>
              </div>

              {/* Job Details */}
              <div>
                <h3 className={`text-xs font-semibold uppercase tracking-widest mb-4 ${subLabelCls}`}>Job Details</h3>
                <div className="space-y-5">
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${labelCls}`}>Job Description <span className="text-red-500">*</span></label>
                    <textarea value={pipelinePostForm.job_description} onChange={e => setPipelinePostForm(p => ({ ...p, job_description: e.target.value }))} rows={5} placeholder="Describe the role, responsibilities, and expectations..." className={`w-full px-4 py-3 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent resize-none ${inputCls}`} />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${labelCls}`}>Skills & Qualifications <span className="text-red-500">*</span></label>
                    <textarea value={pipelinePostForm.skills_qualifications} onChange={e => setPipelinePostForm(p => ({ ...p, skills_qualifications: e.target.value }))} rows={4} placeholder="List required skills, certifications, and experience..." className={`w-full px-4 py-3 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent resize-none ${inputCls}`} />
                  </div>
                </div>
              </div>

              {/* Application Questions */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className={`text-xs font-semibold uppercase tracking-widest ${subLabelCls}`}>Application Questions</h3>
                    <p className={`text-xs mt-1 ${subLabelCls}`}>Applicants will be asked to answer these when applying through the pipeline</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPipelinePostForm(p => ({ ...p, application_questions: [...p.application_questions, { question: '' }] }))}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[#40E0D0] text-black text-sm font-medium rounded-lg hover:bg-[#40E0D0]/90 transition-colors"
                  >
                    <Plus className="w-4 h-4" />Add Question
                  </button>
                </div>
                <div className="space-y-3">
                  {pipelinePostForm.application_questions.map((q, idx) => (
                    <div key={idx} className={`flex items-start gap-3 p-4 rounded-xl border ${isDark ? 'border-gray-600 bg-gray-700/30' : 'border-gray-300 bg-gray-50'}`}>
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-sm mt-0.5 ${isDark ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>{idx + 1}</div>
                      <input
                        type="text"
                        value={q.question}
                        onChange={e => {
                          const updated = [...pipelinePostForm.application_questions];
                          updated[idx] = { question: e.target.value };
                          setPipelinePostForm(p => ({ ...p, application_questions: updated }));
                        }}
                        placeholder="e.g. How many years of Workday HCM experience do you have?"
                        className={`flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${inputCls}`}
                      />
                      {pipelinePostForm.application_questions.length > 1 && (
                        <button type="button" onClick={() => setPipelinePostForm(p => ({ ...p, application_questions: p.application_questions.filter((_, i) => i !== idx) }))} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}>
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* What consultants will see */}
              <div className={`p-4 rounded-xl border ${isDark ? 'border-gray-600 bg-gray-700/30' : 'border-gray-200 bg-gray-50'}`}>
                <p className={`text-xs font-semibold uppercase tracking-widest mb-3 ${subLabelCls}`}>What Consultants Will See</p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#40E0D0] flex-shrink-0" /><span className={isDark ? 'text-gray-300' : 'text-gray-700'}>Your company name and branding (Pro posting)</span></div>
                  <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#40E0D0] flex-shrink-0" /><span className={isDark ? 'text-gray-300' : 'text-gray-700'}>Full job description, skills & qualifications</span></div>
                  <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#40E0D0] flex-shrink-0" /><span className={isDark ? 'text-gray-300' : 'text-gray-700'}>Application form with your custom questions</span></div>
                  <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#40E0D0] flex-shrink-0" /><span className={isDark ? 'text-gray-300' : 'text-gray-700'}>Candidates tracked end-to-end in your pipeline dashboard</span></div>
                  {pipelinePostForm.compensation_amount && <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#40E0D0] flex-shrink-0" /><span className={isDark ? 'text-gray-300' : 'text-gray-700'}>Compensation: ${parseFloat(pipelinePostForm.compensation_amount || '0').toLocaleString()}</span></div>}
                </div>
              </div>

              {/* Posting status */}
              <div className={`flex items-center gap-2 p-4 rounded-xl border ${isDark ? 'border-gray-600 bg-gray-700/30' : 'border-gray-300 bg-gray-50'}`}>
                <label className={`text-sm font-medium ${labelCls}`}>Posting Status:</label>
                <select value={pipelinePostForm.status} onChange={e => setPipelinePostForm(p => ({ ...p, status: e.target.value }))} className={`px-3 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] ${inputCls}`}>
                  <option value="draft">Draft (not visible to consultants)</option>
                  <option value="open">Open (visible and accepting applications)</option>
                </select>
              </div>

              {pipelineFormError && (
                <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                  <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>{pipelineFormError}</span>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={handlePipelinePostSubmit} disabled={submitting} className="flex items-center gap-2 px-6 py-3 bg-[#40E0D0] text-black font-semibold rounded-xl hover:bg-[#40E0D0]/90 transition-colors disabled:opacity-50">
                  <GitMerge className="w-4 h-4" />
                  {submitting ? 'Publishing...' : 'Publish Pipeline Post'}
                </button>
                <button type="button" onClick={() => { setActiveView('list'); setPipelineFormError(''); }} className={`px-6 py-3 rounded-xl font-medium transition-colors ${isDark ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {activeView === 'quick-post' && (
          <div className={`rounded-xl border ${card} overflow-hidden`}>
            <div className={`px-6 py-5 border-b ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-100 bg-gray-50'}`}>
              <div className="flex items-center gap-3 mb-1">
                <button onClick={() => setActiveView('list')} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}><ArrowLeft className="w-4 h-4" /></button>
                <Zap className="w-5 h-5 text-[#40E0D0]" />
                <h2 className="font-bold text-lg">Quick Post via Workday Link</h2>
                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#40E0D0] text-black text-xs font-bold ml-1"><Star className="w-3 h-3" />Pro</span>
              </div>
              <p className={`text-sm ml-10 ${subLabelCls}`}>Link directly to your existing Workday requisition. Consultants see your job data and apply through your Workday portal.</p>
            </div>

            {/* Branding banner — same as list view */}
            <div className={`border-b overflow-hidden ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
              <div className="relative w-full group overflow-hidden" style={{ paddingTop: 'calc(191 / 1584 * 100%)' }}>
                <div className="absolute inset-0">
                  {backgroundImageUrl ? (
                    <img src={backgroundImageUrl} alt="Company background" className="w-full h-full object-cover" />
                  ) : (
                    <div className={`w-full h-full ${isDark ? 'bg-gradient-to-br from-gray-700 to-gray-800' : 'bg-gradient-to-br from-slate-200 to-slate-300'}`} />
                  )}
                  <button
                    onClick={() => backgroundInputRef.current?.click()}
                    disabled={uploadingBackground}
                    className="absolute top-2 right-3 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-black/50 text-white text-xs font-medium hover:bg-black/70 transition-colors opacity-0 group-hover:opacity-100"
                  >
                    {uploadingBackground ? <div className="animate-spin rounded-full h-3 w-3 border-t border-white" /> : <Camera className="w-3.5 h-3.5" />}
                    {uploadingBackground ? 'Uploading...' : 'Change Cover'}
                  </button>
                  <input ref={backgroundInputRef} type="file" accept="image/*" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) handleUploadBrandImage(f, 'background'); e.target.value = ''; }} />
                </div>
              </div>
              <div className="px-6 pb-4">
                <div className="flex items-end gap-4 -mt-8 mb-0">
                  <div className="relative group flex-shrink-0">
                    <div className={`w-20 h-20 sm:w-24 sm:h-24 rounded-xl border-4 overflow-hidden ${isDark ? 'border-gray-800 bg-gray-700' : 'border-white bg-gray-100'} shadow-md`}>
                      {companyProfileImageUrl ? (
                        <img src={companyProfileImageUrl} alt="Company logo" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Building2 className={`w-8 h-8 sm:w-10 sm:h-10 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => profileInputRef.current?.click()}
                      disabled={uploadingProfile}
                      className="absolute inset-0 rounded-xl flex items-center justify-center bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      {uploadingProfile ? <div className="animate-spin rounded-full h-4 w-4 border-t border-white" /> : <Camera className="w-5 h-5" />}
                    </button>
                    <input ref={profileInputRef} type="file" accept="image/*" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) handleUploadBrandImage(f, 'profile'); e.target.value = ''; }} />
                  </div>
                  <div className="pb-1">
                    <p className={`text-xs ${subLabelCls}`}>Your branding appears on this job posting for consultants</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <button onClick={() => backgroundInputRef.current?.click()} className={`flex items-center gap-1.5 px-3 py-1 rounded-lg border text-xs font-medium transition-colors ${isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                        <ImageIcon className="w-3.5 h-3.5" />Cover Photo
                      </button>
                      <button onClick={() => profileInputRef.current?.click()} className={`flex items-center gap-1.5 px-3 py-1 rounded-lg border text-xs font-medium transition-colors ${isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                        <Upload className="w-3.5 h-3.5" />Logo
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-8">
              {/* Explanatory banner */}
              <div className={`flex items-start gap-4 p-4 rounded-xl border ${isDark ? 'border-teal-800 bg-teal-900/20' : 'border-teal-200 bg-teal-50'}`}>
                <Link2 className="w-5 h-5 text-[#40E0D0] flex-shrink-0 mt-0.5" />
                <div>
                  <p className={`text-sm font-semibold mb-1 ${isDark ? 'text-teal-300' : 'text-teal-800'}`}>How Quick Post Works</p>
                  <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    Paste your Workday requisition URL and fill in only the key fields. Consultants browse your posting on PerplexityATS and are directed to apply directly through your company's Workday portal — no duplicate application forms needed.
                  </p>
                </div>
              </div>

              {/* Workday Requisition URL — hero field */}
              <div>
                <label className={`block text-sm font-semibold mb-2 ${labelCls}`}>
                  <span className="flex items-center gap-2"><Link2 className="w-4 h-4 text-[#40E0D0]" />Workday Requisition URL <span className="text-red-500">*</span></span>
                </label>
                <input
                  type="url"
                  value={quickPostForm.career_page_url}
                  onChange={e => setQuickPostForm(p => ({ ...p, career_page_url: e.target.value }))}
                  placeholder="https://wd3.myworkday.com/yourcompany/d/inst/1/ureq/12345"
                  className={`w-full px-4 py-3 border-2 rounded-xl text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-[#40E0D0] transition-colors ${quickPostForm.career_page_url ? (isDark ? 'border-teal-700' : 'border-teal-400') : inputCls}`}
                />
                {quickPostForm.career_page_url && (
                  <a href={quickPostForm.career_page_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-2 text-xs text-[#40E0D0] hover:underline">
                    <ExternalLink className="w-3.5 h-3.5" />Preview requisition link
                  </a>
                )}
              </div>

              {/* Core fields */}
              <div>
                <h3 className={`text-xs font-semibold uppercase tracking-widest mb-4 ${subLabelCls}`}>Requisition Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${labelCls}`}>Workday Requisition # <span className="text-red-500">*</span></label>
                    <input type="text" value={quickPostForm.requisition_number} onChange={e => setQuickPostForm(p => ({ ...p, requisition_number: e.target.value }))} placeholder="e.g. WD-2024-001" className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${inputCls}`} />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${labelCls}`}>Job Title <span className="text-red-500">*</span></label>
                    <input type="text" value={quickPostForm.job_title} onChange={e => setQuickPostForm(p => ({ ...p, job_title: e.target.value }))} placeholder="e.g. Workday HCM Consultant" className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${inputCls}`} />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${labelCls}`}>Hire Deadline <span className="text-red-500">*</span></label>
                    <input type="date" value={quickPostForm.hire_deadline_date} onChange={e => setQuickPostForm(p => ({ ...p, hire_deadline_date: e.target.value }))} className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${inputCls}`} />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${labelCls}`}>Worker Type <span className="text-red-500">*</span></label>
                    <select value={quickPostForm.worker_type} onChange={e => setQuickPostForm(p => ({ ...p, worker_type: e.target.value }))} className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${inputCls}`}>
                      <option value="hire">Full-Time Hire</option>
                      <option value="contract">Contract</option>
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${labelCls}`}>Supervisory Organization</label>
                    <input type="text" value={quickPostForm.supervisory_organization} onChange={e => setQuickPostForm(p => ({ ...p, supervisory_organization: e.target.value }))} placeholder="e.g. Finance & Accounting" className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${inputCls}`} />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${labelCls}`}>Job Family</label>
                    <input type="text" value={quickPostForm.job_family} onChange={e => setQuickPostForm(p => ({ ...p, job_family: e.target.value }))} placeholder="e.g. Technology" className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${inputCls}`} />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${labelCls}`}>Compensation Amount</label>
                    <input type="number" step="0.01" value={quickPostForm.compensation_amount} onChange={e => setQuickPostForm(p => ({ ...p, compensation_amount: e.target.value }))} placeholder="e.g. 120000" className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${inputCls}`} />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${labelCls}`}>Comp Grade</label>
                    <input type="text" value={quickPostForm.comp_grade} onChange={e => setQuickPostForm(p => ({ ...p, comp_grade: e.target.value }))} placeholder="e.g. Grade 5" className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent ${inputCls}`} />
                  </div>
                </div>
              </div>

              {/* What consultants will see preview */}
              <div className={`p-4 rounded-xl border ${isDark ? 'border-gray-600 bg-gray-700/30' : 'border-gray-200 bg-gray-50'}`}>
                <p className={`text-xs font-semibold uppercase tracking-widest mb-3 ${subLabelCls}`}>What Consultants Will See</p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#40E0D0] flex-shrink-0" /><span className={isDark ? 'text-gray-300' : 'text-gray-700'}>Your company name revealed (Pro posting)</span></div>
                  <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#40E0D0] flex-shrink-0" /><span className={isDark ? 'text-gray-300' : 'text-gray-700'}>Job title, requisition #, worker type, deadline</span></div>
                  <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#40E0D0] flex-shrink-0" /><span className={isDark ? 'text-gray-300' : 'text-gray-700'}>Direct link to your Workday requisition to apply</span></div>
                  {quickPostForm.compensation_amount && <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#40E0D0] flex-shrink-0" /><span className={isDark ? 'text-gray-300' : 'text-gray-700'}>Compensation: ${parseFloat(quickPostForm.compensation_amount || '0').toLocaleString()}</span></div>}
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={handleQuickPostSubmit} disabled={submitting} className="flex items-center gap-2 px-6 py-3 bg-[#40E0D0] text-black font-semibold rounded-xl hover:bg-[#40E0D0]/90 transition-colors disabled:opacity-50">
                  <Zap className="w-4 h-4" />
                  {submitting ? 'Publishing...' : 'Publish Quick Post'}
                </button>
                <button type="button" onClick={() => setActiveView('list')} className={`px-6 py-3 rounded-xl font-medium transition-colors ${isDark ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {viewingJob && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col rounded-2xl shadow-2xl ${isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}>
            <div className={`sticky top-0 flex-shrink-0 border-b px-6 py-5 ${isDark ? 'border-gray-600 bg-gray-800' : 'border-gray-300 bg-white'}`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold">{viewingJob.job_title || viewingJob.requisition_number}</h2>
                  <p className={`text-sm mt-0.5 ${subLabelCls}`}>Req #{viewingJob.requisition_number} · <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[viewingJob.status] || ''}`}>{viewingJob.status}</span></p>
                </div>
                <button onClick={() => setViewingJob(null)} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}><X className="w-5 h-5" /></button>
              </div>
              <div className={`flex border-b ${isDark ? 'border-gray-600' : 'border-gray-300'} -mb-px`}>
                {['details', 'applications'].map(tab => (
                  <button key={tab} onClick={() => setSelectedTab(tab as any)} className={`px-5 py-2.5 text-sm font-medium capitalize border-b-2 transition-colors ${selectedTab === tab ? 'border-[#40E0D0] text-[#40E0D0]' : `border-transparent ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-800'}`}`}>
                    {tab === 'applications' ? `Applications (${applicationCounts[viewingJob.id] || 0})` : tab}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {selectedTab === 'details' ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className={`p-4 rounded-xl ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}><p className={`text-xs ${subLabelCls} mb-1`}>Worker Type</p><p className="font-semibold capitalize">{viewingJob.worker_type === 'hire' ? 'Full-Time' : 'Contract'}</p></div>
                    <div className={`p-4 rounded-xl ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}><p className={`text-xs ${subLabelCls} mb-1`}>Deadline</p><p className="font-semibold">{new Date(viewingJob.hire_deadline_date).toLocaleDateString()}</p></div>
                    {viewingJob.compensation_amount && <div className={`p-4 rounded-xl ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}><p className={`text-xs ${subLabelCls} mb-1`}>Compensation</p><p className="font-semibold">${viewingJob.compensation_amount.toLocaleString()}</p></div>}
                    {viewingJob.comp_grade && <div className={`p-4 rounded-xl ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}><p className={`text-xs ${subLabelCls} mb-1`}>Comp Grade</p><p className="font-semibold">{viewingJob.comp_grade}</p></div>}
                  </div>
                  {viewingJob.career_page_url && (
                    <div className={`flex items-center gap-3 p-4 rounded-xl border ${isDark ? 'border-teal-800 bg-teal-900/20' : 'border-teal-200 bg-teal-50'}`}>
                      <Link2 className="w-4 h-4 text-[#40E0D0] flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs font-semibold uppercase tracking-widest mb-0.5 ${subLabelCls}`}>Career Page / Job Requisition URL</p>
                        <a href={viewingJob.career_page_url} target="_blank" rel="noopener noreferrer" className="text-sm text-[#40E0D0] hover:underline flex items-center gap-1 truncate">
                          {viewingJob.career_page_url}<ExternalLink className="w-3 h-3 flex-shrink-0" />
                        </a>
                      </div>
                    </div>
                  )}
                  {viewingJob.supervisory_organization && <div><p className={`text-xs font-semibold uppercase tracking-widest ${subLabelCls} mb-2`}>Supervisory Organization</p><p>{viewingJob.supervisory_organization}</p></div>}
                  {viewingJob.job_family && <div><p className={`text-xs font-semibold uppercase tracking-widest ${subLabelCls} mb-2`}>Job Family</p><p>{viewingJob.job_family}</p></div>}
                  <div><p className={`text-xs font-semibold uppercase tracking-widest ${subLabelCls} mb-2`}>Job Description</p><p className={`whitespace-pre-wrap leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{viewingJob.job_description}</p></div>
                  <div><p className={`text-xs font-semibold uppercase tracking-widest ${subLabelCls} mb-2`}>Skills & Qualifications</p><p className={`whitespace-pre-wrap leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{viewingJob.skills_qualifications}</p></div>
                  {viewingJob.application_questions && viewingJob.application_questions.length > 0 && (
                    <div>
                      <p className={`text-xs font-semibold uppercase tracking-widest ${subLabelCls} mb-3`}>Application Questions</p>
                      <div className="space-y-2">
                        {viewingJob.application_questions.map((q, i) => (
                          <div key={i} className={`flex items-start gap-3 p-3.5 rounded-xl ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                            <HelpCircle className="w-4 h-4 text-[#40E0D0] flex-shrink-0 mt-0.5" />
                            <p className="text-sm">{q.question}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {loadingApplications ? (
                    <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#40E0D0]"></div></div>
                  ) : applications.length === 0 ? (
                    <div className="text-center py-16"><Users className={`w-10 h-10 mx-auto mb-3 ${subLabelCls} opacity-40`} /><p className={subLabelCls}>No applications received yet.</p></div>
                  ) : (
                    applications.map(app => (
                      <div key={app.id} className={`rounded-xl border p-5 ${isDark ? 'border-gray-600' : 'border-gray-300'}`}>
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <div className="flex items-center gap-3 flex-wrap">
                              {isPro ? (
                                <span className="text-base font-bold">{app.first_name} {app.last_name}</span>
                              ) : (
                                <div className={`flex items-center gap-2 px-3 py-1 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-100 border-gray-200'}`}>
                                  <Lock className="w-3.5 h-3.5 text-gray-400" />
                                  <span className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Applicant #{applications.indexOf(app) + 1}</span>
                                </div>
                              )}
                              <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${APP_STATUS_COLORS[app.status] || 'bg-gray-100 text-gray-700'}`}>
                                {app.status === 'pending'
                                  ? (viewingJob?.is_pro_posting ? 'Pending Review' : 'Pending Interview Through Perplexity')
                                  : app.status.replace(/_/g, ' ')}
                              </span>
                              <button
                                onClick={() => navigate(`/workday-client/consultant/${app.consultant_id}`)}
                                className="flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-lg border border-[#40E0D0] text-[#40E0D0] hover:bg-[#40E0D0]/10 transition-colors"
                              >
                                View Profile
                              </button>
                            </div>
                            {isPro ? (
                              <div className="flex flex-wrap items-center gap-3 mt-1.5">
                                {app.title && <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{app.title}</span>}
                                {app.email && <a href={`mailto:${app.email}`} className="flex items-center gap-1 text-xs text-[#40E0D0] hover:underline"><Mail className="w-3.5 h-3.5" />{app.email}</a>}
                                {app.phone && <a href={`tel:${app.phone}`} className="flex items-center gap-1 text-xs text-[#40E0D0] hover:underline"><Phone className="w-3.5 h-3.5" />{app.phone}</a>}
                              </div>
                            ) : (
                              app.title && (
                                <p className={`text-sm mt-1.5 cursor-pointer underline decoration-dotted ${isDark ? 'text-gray-300 hover:text-[#40E0D0]' : 'text-gray-600 hover:text-[#40E0D0]'} transition-colors w-fit`} onClick={() => setShowProUpgradePopup(true)}>
                                  {app.title}
                                </p>
                              )
                            )}
                          </div>
                          <select value={app.status} onChange={e => updateApplicationStatus(app.id, e.target.value)} disabled={updatingStatus === app.id} className={`px-3 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] disabled:opacity-50 ${inputCls}`}>
                            <option value="pending">Pending</option>
                            <option value="reviewed">Reviewed</option>
                            <option value="shortlisted">Pre-Selected</option>
                            <option value="interview_scheduled">Interview Scheduled</option>
                            <option value="rejected">Rejected</option>
                            <option value="hired">Hired</option>
                          </select>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                          <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}><p className={`text-xs ${subLabelCls} mb-0.5`}>Experience</p><p className="font-semibold text-sm">{app.experience_years}</p></div>
                          <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}><p className={`text-xs ${subLabelCls} mb-0.5`}>Rate</p><p className="font-semibold text-sm flex items-center"><DollarSign className="w-3 h-3" />{app.hourly_rate}</p></div>
                          <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}><p className={`text-xs ${subLabelCls} mb-0.5`}>Availability</p><p className="font-semibold text-sm">{app.availability}</p></div>
                          <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}><p className={`text-xs ${subLabelCls} mb-0.5`}>Region</p><p className="font-semibold text-sm">{app.country_region}</p></div>
                        </div>
                        {app.workday_modules && <div className="mb-3"><p className={`text-xs font-semibold ${subLabelCls} mb-1`}>Workday Modules</p><p className="text-sm">{typeof app.workday_modules === 'string' ? app.workday_modules : app.workday_modules.join(', ')}</p></div>}
                        {(app.cover_letter || app.message) && <div className="mb-3"><p className={`text-xs font-semibold ${subLabelCls} mb-1`}>Why They're Qualified</p><div className={`p-3 rounded-lg text-sm ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}><p className="whitespace-pre-wrap">{app.cover_letter || app.message}</p></div></div>}
                        {app.question_answers && app.question_answers.length > 0 && (
                          <div className="mb-3">
                            <p className={`text-xs font-semibold ${subLabelCls} mb-2`}>Application Question Answers</p>
                            <div className="space-y-2">
                              {app.question_answers.map((qa, i) => (
                                <div key={i} className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-blue-50'}`}>
                                  <p className={`text-xs font-semibold mb-1 ${isDark ? 'text-[#40E0D0]' : 'text-blue-700'}`}>{qa.question}</p>
                                  <p className="text-sm">{qa.answer}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {isPro && app.resume_url ? (
                          <a href={app.resume_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-[#40E0D0] text-black text-sm font-medium rounded-lg hover:bg-[#40E0D0]/90 transition-colors">
                            <FileText className="w-4 h-4" />View Resume
                          </a>
                        ) : isPro ? (
                          <span className={`inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg border ${isDark ? 'border-gray-600 text-gray-500' : 'border-gray-200 text-gray-400'}`}>
                            <FileText className="w-4 h-4" />No Resume Uploaded
                          </span>
                        ) : (
                          <button onClick={() => setShowProUpgradePopup(true)} className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${isDark ? 'border-gray-600 text-gray-400 hover:bg-gray-700' : 'border-gray-300 text-gray-500 hover:bg-gray-50'}`}>
                            <Lock className="w-4 h-4" />Resume — Pro Only
                          </button>
                        )}
                        {app.admin_notes && <div className={`mt-3 pt-3 border-t ${isDark ? 'border-gray-600' : 'border-gray-300'}`}><p className={`text-xs font-semibold ${subLabelCls} mb-1`}>Admin Notes</p><p className="text-sm">{app.admin_notes}</p></div>}
                        <div className="mt-4 flex flex-wrap items-center gap-3">
                          {viewingJob?.is_pro_posting ? (
                            <>
                              {/* Pre-Screen status badge */}
                              {app.prescreen_status && (
                                <span className={`text-xs px-2.5 py-1 rounded-full font-semibold border ${
                                  app.prescreen_status === 'confirmed' ? 'bg-emerald-100 text-emerald-800 border-emerald-200' :
                                  app.prescreen_status === 'availability_submitted' ? 'bg-blue-100 text-blue-800 border-blue-200' :
                                  app.prescreen_status === 'rescheduling' ? 'bg-orange-100 text-orange-800 border-orange-200' :
                                  'bg-yellow-100 text-yellow-800 border-yellow-200'
                                }`}>
                                  {app.prescreen_status === 'requested' ? 'Pre-Screen: Awaiting Availability' :
                                   app.prescreen_status === 'availability_submitted' ? 'Pre-Screen: Review Availability' :
                                   app.prescreen_status === 'confirmed' ? 'Pre-Screen: Confirmed' :
                                   app.prescreen_status === 'rescheduling' ? 'Pre-Screen: Rescheduling' : app.prescreen_status}
                                </span>
                              )}
                              {/* Primary action button */}
                              {(!app.prescreen_status || app.prescreen_status === 'rescheduling') ? (
                                <button
                                  onClick={() => setScheduleFor(app)}
                                  disabled={prescreenSent.has(app.id)}
                                  className="flex items-center gap-2 px-4 py-2 bg-[#40E0D0] text-black text-sm font-semibold rounded-lg hover:bg-[#40E0D0]/90 transition-colors disabled:opacity-60"
                                >
                                  <Calendar className="w-4 h-4" />
                                  {prescreenSent.has(app.id) ? 'Request Sent' : 'Schedule Pre-Screen Interview'}
                                </button>
                              ) : app.prescreen_status === 'availability_submitted' ? (
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => openConfirmModal(app)}
                                    className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white text-sm font-semibold rounded-lg hover:bg-emerald-600 transition-colors"
                                  >
                                    <CheckCircle className="w-4 h-4" />Review & Confirm Time
                                  </button>
                                  <button
                                    onClick={() => setProposeTimesFor(app)}
                                    className={`flex items-center gap-2 px-3 py-2 text-sm font-semibold rounded-lg border transition-colors ${isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                                  >
                                    <RefreshCw className="w-4 h-4" />Reschedule
                                  </button>
                                </div>
                              ) : app.prescreen_status === 'confirmed' ? (
                                <div className="flex items-center gap-2">
                                  <span className="flex items-center gap-1.5 px-3 py-2 bg-emerald-50 text-emerald-700 border border-emerald-200 text-sm font-semibold rounded-lg">
                                    <CheckCircle className="w-4 h-4" />Pre-Screen Confirmed
                                  </span>
                                  <button
                                    onClick={() => setProposeTimesFor(app)}
                                    className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${isDark ? 'border-gray-600 text-gray-400 hover:bg-gray-700' : 'border-gray-300 text-gray-500 hover:bg-gray-50'}`}
                                  >
                                    <RefreshCw className="w-3.5 h-3.5" />Reschedule
                                  </button>
                                </div>
                              ) : app.prescreen_status === 'requested' ? (
                                <button
                                  disabled
                                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-500 text-sm font-semibold rounded-lg cursor-not-allowed"
                                >
                                  <Clock className="w-4 h-4" />Awaiting Candidate Response
                                </button>
                              ) : null}
                            </>
                          ) : (
                            <button onClick={() => setRequestingInterviewFor(app)} className="flex items-center gap-2 px-4 py-2 bg-[#40E0D0] text-black text-sm font-semibold rounded-lg hover:bg-[#40E0D0]/90 transition-colors">
                              <Phone className="w-4 h-4" />Request Interview
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {viewingService && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`max-w-3xl w-full max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl ${isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}>
            <div className={`sticky top-0 flex items-start justify-between p-6 border-b ${isDark ? 'border-gray-600 bg-gray-800' : 'border-gray-300 bg-white'}`}>
              <div>
                <h2 className="text-xl font-bold">{viewingService.project_title || 'Development Requirement'}</h2>
                <p className={`text-sm mt-0.5 ${subLabelCls}`}>Submitted {new Date(viewingService.created_at).toLocaleDateString()}</p>
              </div>
              <button onClick={() => setViewingService(null)} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {viewingService.number_of_employees && <div className={`p-4 rounded-xl ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}><p className={`text-xs ${subLabelCls} mb-1`}>Employees</p><p className="font-semibold">{viewingService.number_of_employees}</p></div>}
                {viewingService.implementation_partner && <div className={`p-4 rounded-xl ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}><p className={`text-xs ${subLabelCls} mb-1`}>Partner</p><p className="font-semibold">{viewingService.implementation_partner}</p></div>}
                {viewingService.go_live_date && <div className={`p-4 rounded-xl ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}><p className={`text-xs ${subLabelCls} mb-1`}>Go-Live</p><p className="font-semibold">{new Date(viewingService.go_live_date).toLocaleDateString()}</p></div>}
              </div>
              {viewingService.position_requirements?.length > 0 && (
                <div><p className={`text-xs font-semibold uppercase tracking-widest ${subLabelCls} mb-3`}>Position Requirements</p>
                <div className="space-y-2">{viewingService.position_requirements.map((r, i) => <div key={i} className={`flex gap-4 p-3 rounded-lg text-sm ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}><span className="font-medium">{r.positions}</span><span className={subLabelCls}>{r.type}</span></div>)}</div></div>
              )}
              {viewingService.issues_and_modules && <div><p className={`text-xs font-semibold uppercase tracking-widest ${subLabelCls} mb-2`}>Issues, Modules & Budgets</p><p className={`whitespace-pre-wrap text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{viewingService.issues_and_modules}</p></div>}
              {viewingService.project_requirements && <div><p className={`text-xs font-semibold uppercase tracking-widest ${subLabelCls} mb-2`}>Development Requirement</p><p className={`whitespace-pre-wrap text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{viewingService.project_requirements}</p></div>}
              {viewingService.implementation_stages && <div><p className={`text-xs font-semibold uppercase tracking-widest ${subLabelCls} mb-2`}>Implementation Stages</p><p className={`whitespace-pre-wrap text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{viewingService.implementation_stages}</p></div>}
              {viewingService.enhancements && <div><p className={`text-xs font-semibold uppercase tracking-widest ${subLabelCls} mb-2`}>Enhancements</p><p className={`whitespace-pre-wrap text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{viewingService.enhancements}</p></div>}
            </div>
          </div>
        </div>
      )}

      {requestingInterviewFor && (
        <InterviewRequestModal
          application={requestingInterviewFor}
          onClose={() => setRequestingInterviewFor(null)}
          onSuccess={() => { setRequestingInterviewFor(null); navigate('/workday-client/interview-requests'); }}
        />
      )}

      {/* Confirm Slot modal — client reviews consultant's submitted availability */}
      {confirmFor && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4" onClick={() => setConfirmFor(null)}>
          <div className={`max-w-md w-full rounded-2xl shadow-2xl overflow-hidden ${isDark ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`} onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-5 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold">Confirm Interview Time</h2>
                    <p className="text-white/80 text-xs mt-0.5">{confirmFor.app.first_name} {confirmFor.app.last_name} submitted their availability</p>
                  </div>
                </div>
                <button onClick={() => setConfirmFor(null)} className="p-2 hover:bg-white/20 rounded-lg transition-colors"><X className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Select the time slot that works best. The candidate will receive an immediate confirmation.</p>
              {confirmFor.slots.length === 0 ? (
                <div className={`text-center py-6 rounded-xl ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No availability slots found. The candidate may not have submitted times yet.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {confirmFor.slots.map((s, i) => (
                    <button key={i} onClick={() => confirmPrescreen(s)} disabled={confirmingSlot}
                      className={`w-full flex items-center justify-between px-4 py-3.5 rounded-xl border-2 text-left transition-all group ${isDark ? 'border-gray-700 hover:border-emerald-400 hover:bg-emerald-900/20' : 'border-gray-200 hover:border-emerald-400 hover:bg-emerald-50'} disabled:opacity-50`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDark ? 'bg-gray-700 group-hover:bg-emerald-800/40' : 'bg-gray-100 group-hover:bg-emerald-100'}`}>
                          <Calendar className="w-4 h-4 text-emerald-500" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold">{new Date(s.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
                          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{s.time}</p>
                        </div>
                      </div>
                      <span className={`text-xs font-semibold px-3 py-1 rounded-full transition-colors ${isDark ? 'bg-gray-700 text-gray-400 group-hover:bg-emerald-700 group-hover:text-white' : 'bg-gray-100 text-gray-500 group-hover:bg-emerald-500 group-hover:text-white'}`}>
                        {confirmingSlot ? 'Confirming...' : 'Confirm'}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              <div className="flex gap-2 pt-1">
                <button onClick={() => { setConfirmFor(null); setProposeTimesFor(confirmFor.app); }} className={`flex-1 py-2.5 rounded-xl text-sm font-medium border transition-colors flex items-center justify-center gap-2 ${isDark ? 'border-gray-700 text-gray-400 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                  <RefreshCw className="w-3.5 h-3.5" />None work — propose new times
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Pre-Screen Interview modal (initial request) */}
      {scheduleFor && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4" onClick={() => setScheduleFor(null)}>
          <div className={`max-w-md w-full rounded-2xl shadow-2xl overflow-hidden ${isDark ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`} onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-[#0066FF] to-[#40E0D0] p-5 text-white">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold">Schedule Pre-Screen Interview</h2>
                    <p className="text-white/80 text-xs">{scheduleFor.first_name} {scheduleFor.last_name}</p>
                  </div>
                </div>
                <button onClick={() => setScheduleFor(null)} className="p-2 hover:bg-white/20 rounded-lg transition-colors"><X className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Propose up to 3 available time slots for the candidate to choose from. They will receive a message to confirm their availability.</p>
              {scheduleSlots.map((slot, i) => (
                <div key={i} className="flex gap-3 items-center">
                  <span className={`text-xs font-semibold w-16 flex-shrink-0 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Option {i + 1}</span>
                  <input type="date" value={slot.date} onChange={e => setScheduleSlots(prev => prev.map((s, j) => j === i ? { ...s, date: e.target.value } : s))}
                    className={`flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent outline-none ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'}`} />
                  <input type="time" value={slot.time} onChange={e => setScheduleSlots(prev => prev.map((s, j) => j === i ? { ...s, time: e.target.value } : s))}
                    className={`w-28 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent outline-none ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'}`} />
                </div>
              ))}
              <div>
                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Note to Candidate (optional)</label>
                <textarea value={scheduleNote} onChange={e => setScheduleNote(e.target.value)} rows={2} placeholder="e.g. We're looking forward to speaking with you. Please confirm your availability."
                  className={`w-full px-3 py-2 border rounded-lg text-sm resize-none focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent outline-none ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'}`} />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setScheduleFor(null)} className={`flex-1 py-2.5 rounded-xl text-sm font-medium border transition-colors ${isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>Cancel</button>
                <button onClick={sendPrescreenRequest} disabled={sendingPrescreen === scheduleFor.id}
                  className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-[#40E0D0] text-black hover:bg-[#40E0D0]/90 transition-colors disabled:opacity-50">
                  {sendingPrescreen === scheduleFor.id ? 'Sending...' : 'Send to Candidate'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Propose New Times modal */}
      {proposeTimesFor && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4" onClick={() => setProposeTimesFor(null)}>
          <div className={`max-w-md w-full rounded-2xl shadow-2xl overflow-hidden ${isDark ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`} onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-[#0066FF] to-[#40E0D0] p-5 text-white">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold">Propose New Times</h2>
                    <p className="text-white/80 text-xs">{proposeTimesFor.first_name} {proposeTimesFor.last_name}</p>
                  </div>
                </div>
                <button onClick={() => setProposeTimesFor(null)} className="p-2 hover:bg-white/20 rounded-lg transition-colors"><X className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Propose up to 3 alternative interview times for the candidate to choose from.</p>
              {proposeSlots.map((slot, i) => (
                <div key={i} className="flex gap-3 items-center">
                  <span className={`text-xs font-semibold w-16 flex-shrink-0 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Option {i + 1}</span>
                  <input type="date" value={slot.date} onChange={e => setProposeSlots(prev => prev.map((s, j) => j === i ? { ...s, date: e.target.value } : s))}
                    className={`flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent outline-none ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'}`} />
                  <input type="time" value={slot.time} onChange={e => setProposeSlots(prev => prev.map((s, j) => j === i ? { ...s, time: e.target.value } : s))}
                    className={`w-28 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent outline-none ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'}`} />
                </div>
              ))}
              <div>
                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Note to Candidate (optional)</label>
                <textarea value={proposeNote} onChange={e => setProposeNote(e.target.value)} rows={2} placeholder="e.g. Please let us know which time works best..."
                  className={`w-full px-3 py-2 border rounded-lg text-sm resize-none focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent outline-none ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'}`} />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setProposeTimesFor(null)} className={`flex-1 py-2.5 rounded-xl text-sm font-medium border transition-colors ${isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>Cancel</button>
                <button onClick={sendProposeNewTimes} disabled={sendingPropose || proposeSlots.every(s => !s.date || !s.time)}
                  className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-[#40E0D0] text-black hover:bg-[#40E0D0]/90 transition-colors disabled:opacity-50">
                  {sendingPropose ? 'Sending...' : 'Send to Candidate'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPostRequisitionPopup && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4" onClick={() => setShowPostRequisitionPopup(false)}>
          <div className={`max-w-lg w-full rounded-2xl shadow-2xl overflow-hidden ${isDark ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`} onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-[#0066FF] to-[#40E0D0] p-6 text-white">
              <div className="flex items-center justify-between mb-3">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-white" />
                </div>
                <button onClick={() => setShowPostRequisitionPopup(false)} className="p-2 hover:bg-white/20 rounded-lg transition-colors"><X className="w-5 h-5 text-white" /></button>
              </div>
              <h2 className="text-xl font-bold">Posting a Workday Requisition?</h2>
              <p className="text-white/80 text-sm mt-1">Through Perplexity Team</p>
            </div>
            <div className="p-6 space-y-5">
              <div className={`flex items-start gap-4 p-4 rounded-xl border ${isDark ? 'border-blue-800 bg-blue-900/20' : 'border-blue-200 bg-blue-50'}`}>
                <Users className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className={`text-sm font-semibold mb-1 ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>Recruiting & Direct Hire Placements</p>
                  <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>All recruiting and direct hire placements are handled directly through the <strong>Perplexity Team</strong>. Our team will match, screen, and present qualified Workday professionals for your open roles.</p>
                </div>
              </div>
              {isPro && (
                <div className={`flex items-start gap-3 p-4 rounded-xl border ${isDark ? 'border-teal-800 bg-teal-900/20' : 'border-teal-200 bg-teal-50'}`}>
                  <Zap className="w-5 h-5 text-[#40E0D0] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className={`text-sm font-semibold mb-1 ${isDark ? 'text-teal-300' : 'text-teal-800'}`}>Pro: Quick Post via Workday Link</p>
                    <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Skip the full form — paste your Workday requisition URL and we'll build the posting around it.</p>
                  </div>
                </div>
              )}
              <div className="flex gap-3 pt-1">
                <button onClick={() => { setShowPostRequisitionPopup(false); startNewJob(); }} className="flex-1 py-3 bg-gradient-to-r from-[#0066FF] to-[#40E0D0] text-white font-semibold rounded-xl hover:opacity-90 transition-opacity">
                  Hire through Perplexity
                </button>
                <button onClick={() => setShowPostRequisitionPopup(false)} className={`px-5 py-3 rounded-xl font-medium border transition-colors ${isDark ? 'border-gray-700 text-gray-400 hover:bg-gray-800' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showProUpgradePopup && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4"
          onClick={() => setShowProUpgradePopup(false)}
        >
          <div
            className={`max-w-md w-full rounded-2xl shadow-2xl overflow-hidden ${isDark ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`}
            onClick={e => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-[#40E0D0] to-[#0066FF] p-6 text-white text-center">
              <div className="flex items-center justify-center gap-2 bg-white/15 border border-white/30 rounded-lg px-4 py-2 mb-4 text-sm font-medium">
                <Wrench className="w-4 h-4 flex-shrink-0" />
                <span>Undergoing construction — (Pro) will be available as soon as developments and updates are complete.</span>
              </div>
              <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <Lock className="w-7 h-7 text-white" />
              </div>
              <h2 className="text-xl font-bold">Applicant Contact Restricted</h2>
              <p className="text-white/80 text-sm mt-1">This feature requires a Pro account</p>
            </div>

            <div className="p-6 space-y-4">
              <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Upgrade to <strong>Perplexity ATS Pro</strong> to unlock full applicant contact details including name, email, phone number, and resume access for all candidates.
              </p>

              <div className={`rounded-xl p-4 border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-teal-50 border-teal-100'}`}>
                <div className="flex items-start gap-3">
                  <Star className="w-5 h-5 text-[#40E0D0] flex-shrink-0 mt-0.5" />
                  <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    In <strong>Perplexity ATS Pro</strong> you will still have access to Perplexity's Workday Development contracting and direct-hire placement services.
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <button
                  className="w-full py-3 bg-gradient-to-r from-[#40E0D0] to-[#0066FF] text-white font-semibold rounded-xl hover:opacity-90 transition-opacity"
                  onClick={() => setShowProUpgradePopup(false)}
                >
                  Upgrade to Pro for Full Workday Talent Contact Access
                </button>
                <button
                  className={`w-full py-2.5 text-sm font-medium rounded-xl border transition-colors ${isDark ? 'border-gray-700 text-gray-400 hover:bg-gray-800' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}
                  onClick={() => setShowProUpgradePopup(false)}
                >
                  Maybe Later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
